<template>
  <button
    v-if="canToggleBreakpoint"
    type="button"
    class="debug-breakpoint-toggle"
    :class="{ 'debug-breakpoint-active': hasBreakpoint }"
    :title="hasBreakpoint ? 'Remove breakpoint' : 'Set breakpoint'"
    @click.stop.prevent="toggleBreakpointFromButton"
  >
    <v-icon size="18">{{ hasBreakpoint ? 'mdi-circle' : 'mdi-record-circle-outline' }}</v-icon>
  </button>

  <!-- Message Input Node -->
  <div v-if="data.type === 'messageInput'" class="n8n-node message-node" :class="debugStateClass">

    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="20">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon"
        style="width: 60px; height: 60px;border-radius: 50%; background-color: transparent;border: none;">
        <v-icon size="40">mdi-chat</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Message</div>
      <div class="node-subtitle">User Input</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>
  <!-- Selected AI Models (outside node box, below) -->
  <div v-if="data.type === 'messageInput' && messageInputModels.length > 0" class="message-models-list">
    <div class="message-models-divider"></div>
    <div class="message-models-section-label">
      <span>AI Models</span>
    </div>
    <div v-for="(model, idx) in messageInputModels" :key="model.value" class="message-model-item">
      <v-icon size="12" class="message-model-icon">mdi-robot</v-icon>
      <span class="message-model-no">{{ model.value }}:</span>
      <span class="message-model-name">{{ model.displayName }}</span>
    </div>
  </div>

  <!-- Webhook Trigger Node (source via external API) -->
  <div v-else-if="data.type === 'webhookTrigger'" class="n8n-node webhook-trigger-node" :class="debugStateClass">

    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="20">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon"
        style="width: 60px; height: 60px;border-radius: 50%; background-color: transparent;border: none;">
        <v-icon size="40">mdi-webhook</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Webhook</div>
      <div class="node-subtitle">External API Entry</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- API Node -->
  <div v-else-if="data.type === 'api'" class="n8n-node api-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-api</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Call</div>
      <div class="node-subtitle">External Service</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- Response Output Node -->
  <div v-else-if="data.type === 'response'" class="n8n-node response-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-message-reply-text</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Response</div>
      <div class="node-subtitle">Bot Output</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-response-handle', 'target')"
      style="border-radius: 50%;" :position="Position.Left" :id="HANDLE_IDS.input" />
  </div>

  <!-- AI Model Node -->
  <div v-else-if="data.type === 'model'" class="ai-agent-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div v-if="selected && !isViewMode && canTestAiModel" class="adv-router-test-pill"
      @click.stop="$emit('open-test', id)"
      title="Test this AI model with a sample user message">
      <v-icon size="11" class="adv-router-test-pill-icon">mdi-test-tube</v-icon>
      <span>TEST</span>
    </div>

    <div class="ai-agent-content" @click="toggleModelSelector">
      <div class="ai-agent-icon">
        <v-icon size="28" color>mdi-robot</v-icon>
      </div>
      <div class="ai-agent-text">
        <div class="ai-agent-title">AI Model</div>
        <div class="ai-agent-subtitle">{{ getModelDisplayName() }}</div>
      </div>
    </div>

    <!-- Base Model Prompt Indicator -->
    <div v-if="data.useBaseModelPrompt" class="base-prompt-indicator">
      <v-icon size="15" color="#1a992f" class="mr-1">mdi-text-box-check</v-icon>
      <span style="margin-bottom: 2px;font-weight: 400; font-size: 12px; color:#1a992f;">Base Prompt</span>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom"
      :id="HANDLE_IDS.outputBottom" />

  </div>

  <!-- Orchestrator Node -->
  <div v-else-if="data.type === 'orchestrator'" class="orch-v2" :class="debugStateClass">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="orch-v2-header">
      <div class="orch-v2-icon">
        <v-icon size="20" color="white">mdi-hub</v-icon>
      </div>
      <div class="orch-v2-header-text">
        <div class="orch-v2-title">{{ data.orchestratorName || 'Orchestrator' }}</div>
        <div v-if="data.provider" class="orch-v2-provider">{{ data.provider }}</div>
      </div>
    </div>

    <div v-if="data.model" class="orch-v2-model-row">
      <div class="orch-v2-model-chip">
        <v-icon size="12">mdi-brain</v-icon>
        <span>{{ getOrchestratorModelShortName() }}</span>
      </div>
      <div v-if="Number(data.temperature)" class="orch-v2-param">T {{ data.temperature }}</div>
    </div>
    <div v-else class="orch-v2-placeholder">
      <v-icon size="14" class="orch-v2-placeholder-icon">mdi-brain</v-icon>
      <span>No model configured</span>
    </div>

    <div v-if="hasOrchFeatures" class="orch-v2-features">
      <v-tooltip text="Multi-agent" location="top" v-if="data.enableMultiAgent">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-multi"><v-icon size="13">mdi-account-multiple</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Stream response" location="top" v-if="data.streamResponse">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-stream"><v-icon size="13">mdi-flash</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="JSON response" location="top" v-if="data.jsonResponse">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-json"><v-icon size="13">mdi-code-json</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Cache" location="top" v-if="data.enableCache">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-cache"><v-icon size="13">mdi-cached</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Guardrail" location="top" v-if="data.guardrailPrompt">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-guard"><v-icon size="13">mdi-shield-check</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Time context" location="top" v-if="data.enableTime">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-time"><v-icon size="13">mdi-clock-outline</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Transaction ID" location="top" v-if="data.enableTransactionId">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-txn"><v-icon size="13">mdi-identifier</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Long-term memory" location="top" v-if="data.longTermMemory">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-memory"><v-icon size="13">mdi-memory</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="InputMessage replacing" location="top" v-if="data.enableInputMessageReplace">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-input-msg"><v-icon size="13">mdi-message-text-outline</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="VoicePrompt replacing" location="top" v-if="data.enableVoicePromptReplace">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-voice"><v-icon size="13">mdi-microphone-outline</v-icon></span></template>
      </v-tooltip>
      <v-tooltip text="Language replacing" location="top" v-if="data.enableLanguageReplace">
        <template v-slot:activator="{ props }"><span v-bind="props" class="orch-v2-feat feat-lang"><v-icon size="13">mdi-translate</v-icon></span></template>
      </v-tooltip>
    </div>

    <div v-if="data.systemPrompt" class="orch-v2-prompt">
      <v-icon size="12" class="orch-v2-prompt-icon">mdi-text-box-outline</v-icon>
      <span class="orch-v2-prompt-text">{{ data.systemPrompt }}</span>
    </div>

    <div class="orchestrator-labels">
      <span v-if="!hasOrchestratorContinueConnection()" class="output-label orchestrator-continue-label">Output</span>
      <span v-if="!hasOrchestratorAgentsConnection()" class="output-label orchestrator-agents-label">Agents</span>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left" :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
    <Handle type="source" :class="getHandleClass('agents-bottom', 'orchestrator-agents-bottom-handle')" :position="Position.Bottom" id="agents-bottom" />
  </div>

  <!-- Agent Node -->
  <div v-else-if="data.type === 'agent'" class="agent-v2" :class="debugStateClass">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="agent-v2-header">
      <div class="agent-v2-icon">
        <v-icon size="20" color="white">mdi-robot</v-icon>
      </div>
      <div class="agent-v2-header-text">
        <div class="agent-v2-title">{{ data.agentName || 'Agent' }}</div>
        <div v-if="data.agentDescription" class="agent-v2-desc">{{ data.agentDescription }}</div>
      </div>
    </div>

    <div class="agent-v2-model-line">
      <v-icon size="12">mdi-brain</v-icon>
      <span v-if="data.useCustomAIModel && data.agentModel">{{ getAgentModelShortName() }}</span>
      <span v-else-if="data.useCustomAIModel" class="feat-disabled">No model set</span>
      <span v-else class="agent-v2-model-inherit">Orchestrator Model</span>
    </div>

    <div class="agent-v2-features">
      <span :class="['agent-v2-feat', data.enableAgentToolDecision ? 'feat-auto' : 'feat-all']">
        <v-icon size="11">{{ data.enableAgentToolDecision ? 'mdi-auto-fix' : 'mdi-playlist-check' }}</v-icon>
        {{ data.enableAgentToolDecision ? 'AUTO' : 'ALL' }}
      </span>

      <span v-if="data.agentEnableTime" class="agent-v2-feat feat-time">
        <v-icon size="11">mdi-clock-outline</v-icon>
        TIME
      </span>
    </div>

    <div class="agent-labels">
      <span v-if="!hasAgentToolsConnection()" class="output-label agent-tools-label">Tools</span>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
    <Handle type="source" :class="getHandleClass('tools-bottom', 'agent-tools-bottom-handle')" :position="Position.Bottom" id="tools-bottom" />
  </div>

  <!-- Intent AI Model Node -->
  <div v-else-if="data.type === 'intentAI'" class="n8n-node intent-ai-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-target-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Intent AI</div>
      <div class="node-subtitle">{{ intentModelDisplay }}</div>
    </div>
    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'intentai-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Query Rewriter Node -->
  <div v-else-if="data.type === 'queryRewriter'" class="query-rewriter-node" :class="debugStateClass">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="selected && !isViewMode && canTestQueryRewriter" class="adv-router-test-pill"
      @click.stop="$emit('open-test', id)"
      title="Test this Query Rewriter with a sample user message">
      <v-icon size="11" class="adv-router-test-pill-icon">mdi-test-tube</v-icon>
      <span>TEST</span>
    </div>
    
      <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="query-rewriter-content">
      <div class="query-rewriter-icon">
        <v-icon size="28" color="white">mdi-message-text-clock-outline</v-icon>
      </div>
      <div class="query-rewriter-text">
        <div class="query-rewriter-title">Query Rewriter</div>
        <div class="query-rewriter-subtitle">{{ getQueryRewriterModeDisplay() }}</div>
      </div>
    </div>

    <div class="query-rewriter-indicators">
      <div class="query-rewriter-indicator" :title="data.queryRewriterMode === 'continueQuestion' ? 'Continue Question Mode' : 'History Summary Mode'">
        <v-icon size="14" :color="data.queryRewriterMode === 'continueQuestion' ? '#7c3aed' : '#a855f7'">
          {{ data.queryRewriterMode === 'continueQuestion' ? 'mdi-message-reply' : 'mdi-history' }}
        </v-icon>
        <span>{{ data.queryRewriterMode === 'continueQuestion' ? 'Context' : 'Summary' }}</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left" :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- Intent Node -->
  <div v-else-if="data.type === 'intent'" class="intent-modern-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div v-if="selected && !isViewMode && canTestIntentRouter" class="adv-router-test-pill"
      @click.stop="$emit('open-test', id)"
      title="Test this Intent Router with a sample user message">
      <v-icon size="11" class="adv-router-test-pill-icon">mdi-test-tube</v-icon>
      <span>TEST</span>
    </div>

    <!-- Header Section -->
    <div class="intent-header-section">
      <div class="intent-header">
        <div class="intent-icon">
          <v-icon size="24">mdi-crosshairs-question</v-icon>
        </div>
        <div class="intent-info">
          <div class="intent-title">Intent Router</div>
          <div class="intent-subtitle">{{ data.intentGroupName || 'New Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Intents Section as Table -->
    <div class="intents-container" v-if="data.intents && data.intents.length > 0">
      <table class="intent-table">
        <thead class="sr-only">
          <tr>
            <th scope="col">Intent</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <template v-for="(intent, intentIndex) in data.intents" :key="intent.id">
          <!-- Main Action Row -->
          <tr v-if="intent.mainAction" class="handle-row">
            <td :rowspan="getIntentActionsCount(intent)" class="intent-cell">
              <div class="intent-content">
                <v-avatar size="small" color="indigo" class="intent-number">
                  <span class="text-caption">{{ intentIndex + 1 }}</span>
                </v-avatar>
                <div class="intent-details">
                  <div class="intent-name">{{ intent.name.length > 50 ? intent.name.substring(0, 50) + '...' :
                    intent.name }}</div>
                  <span v-if="intent.priority || intent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Prority: {{ intent.priority }}
                  </span>
                </div>
              </div>
            </td>
            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-main`, 'intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-main`">
                  <span class="handle-label">{{ intent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <!-- Parallel Action Rows -->
          <tr v-for="(parallel, parallelIndex) in intent.parallelActions" :key="parallel.id" class="handle-row">
            <td class="handle-cell">
              <div class="handle-content parallel-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-${parallel.id}`, 'intent-action-handle parallel-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-${parallel.id}`">
                  <span class="handle-label">{{ parallel.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="intentIndex < data.intents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>


    <!-- Fallback Output Section -->
    <div class="intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">No intent match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />

    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      :id="HANDLE_IDS.outputBottom" />
  </div>

  <!-- Smart Intent Node -->
  <div v-else-if="data.type === 'smartIntent'" class="smart-intent-modern-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div v-if="selected && !isViewMode && canTestSmartIntent" class="adv-router-test-pill"
      @click.stop="$emit('open-test', id)"
      title="Test this Smart Intent with a sample user message">
      <v-icon size="11" class="adv-router-test-pill-icon">mdi-test-tube</v-icon>
      <span>TEST</span>
    </div>

    <!-- Header Section -->
    <div class="smart-intent-header-section">
      <div class="smart-intent-header">
        <div class="smart-intent-icon">
          <v-icon size="24">mdi-robot-confused</v-icon>
        </div>
        <div class="smart-intent-info">
          <div class="smart-intent-title">Smart Intent</div>
          <div class="smart-intent-subtitle">{{ data.smartIntentGroupName || 'New Smart Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Smart Intents Section as Table -->
    <div class="smart-intents-container" v-if="data.smartIntents && data.smartIntents.length > 0">
      <table class="smart-intent-table">
        <thead class="sr-only">
          <tr>
            <th scope="col">Smart Intent</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <template v-for="(smartIntent, smartIntentIndex) in data.smartIntents" :key="smartIntent.id">
          <!-- Main Action Row -->
          <tr v-if="smartIntent.mainAction" class="handle-row">
            <td :rowspan="1" class="smart-intent-cell">
              <div class="smart-intent-content">
                <v-avatar size="small" color="indigo" class="smart-intent-number">
                  <span class="text-caption">{{ smartIntentIndex + 1 }}</span>
                </v-avatar>
                <div class="smart-intent-details">
                  <div class="smart-intent-name">{{ smartIntent.name.length > 50 ? smartIntent.name.substring(0, 50) +
                    '...' : smartIntent.name }}</div>
                  <span v-if="smartIntent.priority || smartIntent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Priority: {{ smartIntent.priority }}
                  </span>
                  <!-- AI Model Info Display -->
                  <div
                    v-if="smartIntent.mainAction.useAI && (smartIntent.mainAction.provider || smartIntent.mainAction.model)"
                    class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentActionModelDisplay(smartIntent.mainAction) }}
                  </div>

                  <!-- Knowledge Sources Icons for this Smart Intent -->
                  <div v-if="hasSmartIntentKnowledgeSources(smartIntent)" class="knowledge-sources-indicators"
                    style="margin-top: 4px;">
                    <div class="knowledge-icons-row">
                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDocuments" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="blue" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-file-document</v-icon>
                          </v-avatar>
                        </template>
                        <span>Documents Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDatabaseIntegration"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="green" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-database</v-icon>
                          </v-avatar>
                        </template>
                        <span>Database Integration</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableProducts" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="purple" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-package-variant</v-icon>
                          </v-avatar>
                        </template>
                        <span>Products Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableApiKnowledge"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="orange" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-rocket-launch</v-icon>
                          </v-avatar>
                        </template>
                        <span>API Knowledge</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </td>

            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${smartIntent.id}-main`, 'smart-intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${smartIntent.id}-main`">
                  <span class="handle-label">{{ smartIntent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <tr v-if="smartIntentIndex < data.smartIntents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Fallback Output Section -->
    <div class="smart-intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">
              Fallback
              <!-- Fallback AI Model Info Display -->
              <div v-if="data.smartIntentFallback && data.smartIntentFallback.useAI && (data.smartIntentFallback.provider || data.smartIntentFallback.model)"
                class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                <div style="display: flex; flex-direction: column;">
                  <div>
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentFallbackModelDisplay() }}
                  </div>
                  <!-- Knowledge Sources Icons for Fallback -->
                  <div v-if="hasSmartIntentFallbackKnowledgeSources()" class="knowledge-sources-icons mt-1">
                    <v-tooltip v-if="hasFallbackDocumentsKnowledge()" text="Documents Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="blue" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-file-document</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackDatabaseKnowledge()" text="Database Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="green" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-database</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackProductsKnowledge()" text="Products Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="purple" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-package-variant</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackApiKnowledge()" text="API Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="orange" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-rocket-launch</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div class="fallback-output-description">No intent match</div>

          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('smart-intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="smart-intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      :id="HANDLE_IDS.outputBottom" />
  </div>

  <!-- Advanced Router Node -->
  <div v-else-if="data.type === 'advancedRouter'" class="adv-router-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Test Pill (top-right; only visible when selected + has prompt + has intents) -->
    <div v-if="selected && !isViewMode && canTestAdvRouter" class="adv-router-test-pill"
      @click.stop="$emit('open-test', id)"
      title="Test classifier prompt with a sample user message">
      <v-icon size="11" class="adv-router-test-pill-icon">mdi-test-tube</v-icon>
      <span>TEST</span>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="adv-router-header-section">
      <div class="adv-router-header">
        <div class="adv-router-icon">
          <v-icon size="24">mdi-source-branch</v-icon>
        </div>
        <div class="adv-router-info">
          <div class="adv-router-title">Advanced Router</div>
          <div class="adv-router-subtitle">{{ data.advancedRouterName || 'New Advanced Router' }}</div>
          <div class="adv-router-meta">
            <v-chip size="x-small" color="purple" variant="tonal" class="adv-router-chip">
              ≥{{ Math.round(((data.advancedRouterConfidenceThreshold != null ? data.advancedRouterConfidenceThreshold : 0.5)) * 100) }}%
            </v-chip>
            <v-chip size="x-small" color="indigo" variant="tonal" class="adv-router-chip">
              L{{ data.advancedRouterMaxDepth || 2 }}
            </v-chip>
            <span v-if="isPreviewNode" class="adv-router-preview-tag" title="This node is a preview/beta feature">PREVIEW</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Hierarchical Outputs -->
    <div class="adv-router-outputs" v-if="data.advancedRouterOutputs && data.advancedRouterOutputs.length > 0">
      <div v-for="entry in getVisibleOutputs(data.advancedRouterOutputs)" :key="entry.id" class="adv-router-row"
        :class="{ 'adv-router-leaf': entry.isLeaf, 'adv-router-parent': !entry.isLeaf }"
        :style="{ paddingLeft: (12 + (entry.depth - 1) * 16) + 'px' }">
        <span class="adv-router-toggle" v-if="!entry.isLeaf" @click.stop="toggleAdvancedRouterCollapse(entry.id)"
          :title="entry.isCollapsed ? 'Expand' : 'Collapse'">
          <v-icon size="14">{{ entry.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
        </span>
        <span class="adv-router-spacer" v-else></span>
        <span class="adv-router-depth">L{{ entry.depth }}</span>
        <span class="adv-router-name" :title="entry.intentName">{{ entry.intentName || 'Intent' }}</span>
        <Handle type="source"
          :class="getHandleClass('advrouter-' + entry.id, 'adv-router-output-handle' + (entry.isCollapsed ? ' hidden-child-handle' : ''))"
          :position="Position.Right" :id="'advrouter-' + entry.id" />

      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="adv-router-empty">
      <v-icon size="16">mdi-information-outline</v-icon>
      <span>No intents configured</span>
    </div>

    <!-- Fallback Output -->
    <div class="adv-router-fallback">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">No intent above threshold</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('advrouter-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="advrouter-fallback" />
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />

    <!-- Bottom Handle for IntentAI plugin -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      :id="HANDLE_IDS.outputBottom" />
  </div>

  <!-- Condition Node -->
  <div v-else-if="data.type === 'condition'" class="n8n-node condition-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-swap-horizontal-bold</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">If</div>
      <div class="node-subtitle">Logic Check</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output-true', 'output-handle condition-true-output-handle')"
      :position="Position.Right" style="top: 25%;" id="output-true" />
    <Handle type="source" :class="getHandleClass('output-false', 'output-handle condition-false-output-handle')"
      :position="Position.Right" style="top: 75% !important;" id="output-false" />

    <!-- Output Labels for Condition -->
    <div class="output-labels condition-labels">
      <span class="output-label condition-true-label" style="top: 0;">{{ data.trueLabel || 'True' }}</span>
      <span class="output-label condition-false-label">{{ data.falseLabel || 'False' }}</span>
    </div>
  </div>

  <!-- JSON Condition Node -->
  <div v-else-if="data.type === 'jsonCondition'" class="n8n-node json-condition-node" :class="debugStateClass"
    :title="data.jsonConditionName || getJsonConditionPreview(data) || 'JSON Condition'">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon json-condition-node-icon">
        <v-icon size="28">mdi-code-json</v-icon>
      </div>
    </div>

    <div class="node-body json-condition-node-body">
      <div class="node-title">{{ data.jsonConditionName || 'Rule-Based' }}</div>
      <div class="node-subtitle json-condition-preview">{{ getJsonConditionPreview(data) }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output-match', 'output-handle json-condition-match-handle')"
      :position="Position.Right" style="top: 29%;" id="output-match" />
    <Handle type="source" :class="getHandleClass('output-fallback', 'output-handle json-condition-fallback-handle')"
      :position="Position.Right" style="top: 79% !important;" id="output-fallback" />

    <div class="output-labels json-condition-labels">
      <span class="output-label json-condition-match-label">{{ data.matchLabel || 'Match' }}</span>
      <span class="output-label json-condition-fallback-label">{{ data.fallbackLabel || 'Fallback' }}</span>
    </div>
  </div>

  <!-- Delay Node -->
  <div v-else-if="data.type === 'delay'" class="n8n-node delay-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-timer</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Delay</div>
      <div class="node-subtitle">
        <span v-if="data.delayType === 'fixed'">
          {{ data.delayValue || 5 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'random'">
          {{ data.randomMin || 1 }}-{{ data.randomMax || 10 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'variable'">
          Variable Delay
        </span>
        <span v-else>
          Time Wait
        </span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- Switch Node -->
  <div v-else-if="data.type === 'switch'" class="switch-modern-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="switch-header-section">
      <div class="switch-header">
        <div class="switch-icon">
          <v-icon size="24">mdi-arrow-decision</v-icon>
        </div>
        <div class="switch-info">
          <div class="switch-title">Switch</div>
          <div class="switch-subtitle">Multi-Path</div>
        </div>
      </div>
    </div>

    <!-- Switch Outputs Section as Table -->
    <div class="switch-outputs-container" v-if="data.switchOutputs && data.switchOutputs.length > 0">
      <table class="switch-table">
        <thead class="sr-only">
          <tr>
            <th scope="col">Switch Output</th>
            <th scope="col">Handle</th>
          </tr>
        </thead>
        <template v-for="(output, outputIndex) in data.switchOutputs" :key="output.id">
          <tr class="handle-row">
            <td class="switch-output-cell">
              <div class="switch-output-content">
                <v-avatar size="small" color="indigo" class="output-number">
                  <span class="text-caption">{{ outputIndex + 1 }}</span>
                </v-avatar>
                <div class="switch-output-details">
                  <div class="switch-output-name">{{ output.name.length > 40 ? output.name.substring(0, 40) + '...' :
                    output.name }}</div>
                  <div class="switch-output-condition">{{ output.condition.length > 35 ? output.condition.substring(0,
                    35) + '...' : output.condition }}</div>
                </div>
              </div>
            </td>
            <td class="handle-cell" style="width: 10px;">
              <div class="handle-content switch-handle">
                <Handle type="source" :class="getHandleClass(`switch-output-${output.id}`, 'switch-output-handle')"
                  :position="Position.Right" :id="`switch-output-${output.id}`">
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="outputIndex < data.switchOutputs.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; width: 10px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Default Case Output (Fixed Handle) -->
    <div class="switch-default-output">
      <div class="default-output-row">
        <div class="default-output-content">
          <v-avatar size="small" color="orange" class="default-output-number">
            <span class="text-caption">D</span>
          </v-avatar>
          <div class="default-output-details">
            <div class="default-output-name">Default Case</div>
            <div class="default-output-description">Fallback path</div>
          </div>
        </div>
        <div class="default-handle-cell">
          <Handle type="source" :class="getHandleClass('switch-default', 'default-output-handle')"
            :position="Position.Right" id="switch-default">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
  </div>

  <!-- Webhook Node -->
  <div v-else-if="data.type === 'webhook'" class="n8n-node webhook-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-webhook</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Webhook</div>
      <div class="node-subtitle">HTTP Notify</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- Email Node -->
  <div v-else-if="data.type === 'email'" class="n8n-node email-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Action Badge -->
    <div class="action-badge">
      <v-icon size="10">mdi-lightning-bolt</v-icon>
      <span>ACTION</span>
    </div>

    <!-- Status Indicator -->
    <div class="status-indicator" :class="{ 'configured': data.toEmail && data.subject }">
      <div class="status-dot"></div>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <div class="icon-bg">
          <v-icon size="28">mdi-email-send</v-icon>
        </div>
      </div>
      <div class="node-details">
        <span v-if="data.useCentralConfig !== false" class="provider-tag central">Central</span>
        <span v-else-if="data.mailConfig && (data.mailConfig.emailSendingMethod || '').toLowerCase() === 'mailgun'" class="provider-tag mailgun">Mailgun</span>
        <span v-else-if="data.mailConfig && (data.mailConfig.emailSendingMethod || '').toLowerCase() === 'smtp'" class="provider-tag smtp">SMTP</span>
        <span v-else class="provider-tag custom">Custom</span>
        <span v-if="isPreviewNode" class="email-preview-tag" title="This node is a preview/beta feature">PREVIEW</span>
        <span v-if="data.ccEmails && data.ccEmails.length > 0" class="cc-indicator">+{{ data.ccEmails.length }}
          CC</span>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Email</div>
      <div class="node-subtitle">
        <span v-if="data.toEmail">{{ data.toEmail.length > 20 ? data.toEmail.substring(0, 20) + '...' : data.toEmail
          }}</span>
        <span v-else>Configure recipient</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- Merge Node -->
  <div v-else-if="data.type === 'merge'" class="n8n-node merge-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Merge Type Badge -->
    <div class="merge-badge">
      <v-icon size="10">mdi-vector-combine</v-icon>
      <span>MERGE</span>
    </div>

    <!-- Input Counter -->
    <div class="input-counter">
      <v-icon size="12">mdi-arrow-collapse-right</v-icon>
      <span>{{ getInputConnectionCount() }}</span>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <div class="icon-bg">
          <v-icon size="28">mdi-call-merge</v-icon>
        </div>
      </div>
      <div class="node-details">
        <span v-if="data.mergeStrategy === 'concatenate'" class="strategy-tag concat">Concatenate</span>
        <span v-else-if="data.mergeStrategy === 'summary'" class="strategy-tag summary">Summary</span>
        <span v-else class="strategy-tag custom">Custom</span>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Merge</div>
      <div class="node-subtitle">
        <span v-if="data.mergeStrategy">{{ getMergeStrategyLabel(data.mergeStrategy) }}</span>
        <span v-else>Combine Results</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.output" />
  </div>

  <!-- API Trigger Node -->
  <div v-else-if="data.type === 'apiTrigger'" class="n8n-node api-trigger-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Trigger</div>
      <div class="node-subtitle">External Action</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.outputSuccess" />
  </div>

  <!-- Contact Agent Node -->
  <div v-else-if="data.type === 'contactAgent'" class="n8n-node contact-agent-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-account-supervisor</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Contact Agent</div>
      <div class="node-subtitle">To disabled bot</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      :id="HANDLE_IDS.input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" :id="HANDLE_IDS.outputSuccess" />
    <!-- No output handle as this is typically an end state -->
  </div>

  <!-- Documents Node -->
  <div v-else-if="data.type === 'documents'" class="n8n-node documents-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-file-document-multiple</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Documents</div>
      <div class="node-subtitle">Document Search</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Database Integration Node -->
  <div v-else-if="data.type === 'databaseIntegration'" class="n8n-node database-integration-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-database-search</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">DB Integration</div>
      <div class="node-subtitle">Knowledge Base</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Knowledge API Node -->
  <div v-else-if="data.type === 'apiknowledge'" class="n8n-node knowledge-api-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Knowledge</div>
      <div class="node-subtitle">API Response Knowledge</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Products Node -->
  <div v-else-if="data.type === 'products'" class="n8n-node products-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-package-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Products</div>
      <div class="node-subtitle">Product Catalog</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Knowledge Graph Node (S15-P1 · knowledge plugin ใต้ AI Model · target-only) -->
  <div v-else-if="data.type === 'knowledgeGraph'" class="n8n-node knowledge-graph-node" :class="debugStateClass"
    data-testid="flow-node-knowledge-graph">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28" color="#7c3aed">mdi-graph-outline</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ $t('flowpg_nodeTypeKnowledgeGraph') }}</div>
      <div class="node-subtitle">{{ $t('flowpg_kgGraphsSelected', { n: (data.knowledgeGraphIds || []).length }) }}</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'knowledge-graph-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Waterfall Search Node (S15-P1 · knowledge plugin ใต้ AI Model · target-only) -->
  <div v-else-if="data.type === 'nexaWaterfallSearch'" class="n8n-node waterfall-search-node" :class="debugStateClass"
    data-testid="flow-node-waterfall-search">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28" color="#0f766e">mdi-stairs</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ $t('flowpg_nodeTypeWaterfallSearch') }}</div>
      <div class="node-subtitle">{{ $t('flowpg_wfTiersCount', { n: (data.graphTiers || []).length }) }}</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'waterfall-search-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Tool Node -->
  <div v-else-if="data.type === 'tool'" class="n8n-node tool-node" :class="debugStateClass">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon tool-node-icon">
        <v-icon size="28">mdi-wrench</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ data.toolName || 'Tool' }}</div>
      <div class="node-subtitle">{{ getToolTypeLabel() }}</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Flow Tool Node -->
  <div v-else-if="data.type === 'flowTool'" class="n8n-node flow-tool-node" :class="debugStateClass">
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <div v-if="data.showWarning" class="warning-badge" title="Internal flow is incomplete">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon flow-tool-node-icon">
        <v-icon size="28">mdi-sitemap</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ data.flowToolName || 'Flow Tool' }}</div>
      <div class="node-subtitle">
        <span v-if="getInternalNodeCount() === 0" class="unconfigured">No internal flow</span>
        <span v-else>{{ getInternalNodeCount() }} internal node{{ getInternalNodeCount() > 1 ? 's' : '' }}</span>
      </div>
      <div v-if="data.internalNodes && data.internalNodes.length" class="flow-tool-stats">
        <v-chip size="x-small" :color="data.showWarning ? 'warning' : 'success'" variant="tonal">
          {{ data.showWarning ? 'Incomplete' : 'Ready' }}
        </v-chip>
      </div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Flow Input Node -->
  <div v-else-if="data.type === 'flowInput'" class="flow-state-node flow-input-state" :class="debugStateClass">
    <div class="hexagon-shape">
      <div class="hexagon-content">
        <v-icon size="26" color="white">mdi-login-variant</v-icon>
        <span class="state-label">Flow Input</span>
      </div>
    </div>
    <Handle type="source" class="flow-state-handle-anchor flow-state-handle-anchor-right" :position="Position.Right" :id="HANDLE_IDS.output">
      <div :class="getHandleClass('output', 'flow-state-handle')"></div>
    </Handle>
  </div>

  <!-- Flow Output Node -->
  <div v-else-if="data.type === 'flowOutput'" class="flow-state-node flow-output-state" :class="debugStateClass">
    <div class="hexagon-shape">
      <div class="hexagon-content">
        <v-icon size="26" color="white">mdi-logout-variant</v-icon>
        <span class="state-label">Flow Output</span>
      </div>
    </div>
    <Handle type="target" class="flow-state-handle-anchor flow-state-handle-anchor-left" :position="Position.Left" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'flow-state-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- SharePoint Node -->
  <div v-else-if="data.type === 'sharepoint'" class="n8n-node sharepoint-node" :class="debugStateClass">
    <!-- Delete Button -->
    <div v-if="!isViewMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Preview Badge -->
    <div v-if="isPreviewNode" class="preview-badge" title="This node is a preview/beta feature">PREVIEW</div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-microsoft-sharepoint</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">SharePoint</div>
      <div v-if="sharepointSiteName || sharepointFileName">
        <div v-if="sharepointSiteName" class="node-subtitle" style="font-weight: 500;">{{ sharepointSiteName }}</div>
        <div v-if="sharepointFileName" class="node-subtitle" style="font-weight: 400;">{{ sharepointFileName }}</div>
      </div>
      <div v-else class="node-subtitle">{{ sharepointDisplayText }}</div>
    </div>

    <Handle type="target" :position="Position.Top" :id="HANDLE_IDS.input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>
</template>

<script>
import { Handle, Position } from '@vue-flow/core'
import { getJsonConditionOperatorToken } from '@/utils/flowUtils';

const HANDLE_IDS = Object.freeze({
  input: 'input',
  output: 'output',
  outputBottom: 'output-bottom',
  outputSuccess: 'output-success',
})

export default {
  name: 'FlowNode',
  components: {
    Handle,
  },
  inject: {
    aiModelOptions: {
      default: () => []
    },
    availableAgentTools: {
      default: () => []
    },
    flowEdges: {
      default: () => []
    },
    // Debug mode injects
    isDebugMode: { default: false },
    debugNodeStates: { default: () => ({}) },
    debugBreakpoints: { default: () => new Set() },
    debugStatus: { default: '' },
    debugOverrideNextNodeId: { default: '' },
    debugHoveredNodeId: { default: '' },
    debugCurrentNodeId: { default: '' },
    toggleDebugBreakpoint: { default: null },
    setDebugHoveredNodeId: { default: null },
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    flowEdgesOverride: {
      type: Array,
      default: null,
    },
    data: {
      type: Object,
      required: false,
      default: () => ({
        type: 'default',
        label: '',
      }),
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['delete-node', 'update:data', 'remove-outgoing-edge', 'open-test'],
  setup(props) {
    // Expose Position for template usage
    return { Position, HANDLE_IDS }
  },
  data() {
    return {
      showModelSelector: true,
      selectedModel: this.data.selectedModel || null,
      advancedRouterCollapsed: {},
      // Node types marked as preview/beta — adjust this list to mark/unmark types.
      previewNodeTypes: ['advancedRouter', 'email'],
    };
  },
  watch: {
    'data.selectedModel': {
      handler(newVal) {
        this.selectedModel = newVal;
      },
      immediate: true
    },
    // Watch for changes in flow edges to handle apiTrigger logic
    activeFlowEdges: {
      handler() {
        // Only for specific node types; avoid scheduling on every node for every edge change
        if (this.data.type === 'apiTrigger' || this.data.type === 'contactAgent') {
          this.$nextTick(() => {
            this.removeOutgoingEdgesIfFromParallelAction();
          });
        }
      },
      // Edge array is typically replaced; deep watch not needed and is expensive
      deep: false,
      immediate: false
    },
    hasBreakpoint() {
      this.$nextTick(() => this.updateBreakpointClass());
    },
    debugState() {
      this.$nextTick(() => this.updateDebugStateHostClass());
    },
    isDebugMode() {
      this.$nextTick(() => {
        this.updateBreakpointClass();
        this.updateDebugStateHostClass();
      });
    },
  },
  mounted() {
    // Check and remove outgoing edges on component mount
    this.$nextTick(() => {
      this.removeOutgoingEdgesIfFromParallelAction();
      this.updateBreakpointClass();
      this.updateDebugStateHostClass();
    });
  },
  beforeUnmount() {
    this.clearDebugStateHostClass();
  },
  computed: {
    activeFlowEdges() {
      return Array.isArray(this.flowEdgesOverride) ? this.flowEdgesOverride : (this.flowEdges || []);
    },
    isViewMode() {
      return this.$route?.query?.mode === 'view';
    },
    isPreviewNode() {
      return Array.isArray(this.previewNodeTypes) && this.previewNodeTypes.includes(this.data?.type);
    },
    hasOrchFeatures() {
      return !!(
        this.data?.enableMultiAgent ||
        this.data?.streamResponse ||
        this.data?.jsonResponse ||
        this.data?.enableCache ||
        this.data?.guardrailPrompt ||
        this.data?.enableTime ||
        this.data?.enableTransactionId ||
        this.data?.enableInputMessageReplace ||
        this.data?.enableVoicePromptReplace ||
        this.data?.enableLanguageReplace ||
        this.data?.longTermMemory
      );
    },
    canTestAiModel() {
      return this.data?.type === 'model' && !!this.data.model;
    },
    canTestIntentRouter() {
      if (this.data?.type !== 'intent') return false;
      const intents = Array.isArray(this.data.intents) ? this.data.intents : [];
      return intents.some(intent => intent && (intent.name || '').trim());
    },
    canTestSmartIntent() {
      if (this.data?.type !== 'smartIntent') return false;
      const smartIntents = Array.isArray(this.data.smartIntents) ? this.data.smartIntents : [];
      return smartIntents.some(smartIntent => smartIntent && (smartIntent.name || '').trim());
    },
    canTestAdvRouter() {
      if (this.data?.type !== 'advancedRouter') return false;
      const prompt = (this.data.advancedRouterPrompt || '').trim();
      const outputs = Array.isArray(this.data.advancedRouterOutputs) ? this.data.advancedRouterOutputs : [];
      return !!prompt && outputs.length > 0;
    },
    canTestQueryRewriter() {
      return this.data?.type === 'queryRewriter' && !!this.data.queryRewriterModel;
    },
    intentModelDisplay() {
      // ใช้ intentai properties (prioritize new format, fallback to legacy)
      const provider = this.data.intentaiProvider;
      const model = this.data.intentaiModel;

      if (!provider && !model) return 'No Model Selected';

      // แสดงทั้ง provider และ model ถ้ามีทั้งคู่
      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model Selected';
    },
    sharepointSiteName() {
      if (this.data.sharepoint.siteUrl) {
        return this.extractSiteNameFromUrl(this.data.sharepoint.siteUrl);
      }
      return '';
    },
    sharepointFileName() {
      if (this.data.sharepoint.filename) {
        return this.data.sharepoint.filename;
      }
      return '';
    },
    sharepointDisplayText() {
      // เก็บไว้เพื่อความเข้ากันได้เดิม - แสดง "File Access" หากไม่มีข้อมูล
      if (!this.data.sharepoint.siteUrl && !this.data.sharepoint.filename) {
        return 'File Access';
      }
      return '';
    },
    // Debug computed properties
    debugState() {
      if (!this.isDebugMode) return null;
      return this.debugNodeStates[this.id] || 'idle';
    },
    debugStateClass() {
      const state = this.debugState;
      if (!state || state === 'idle') return '';
      return `debug-state-${state}`;
    },
    canToggleBreakpoint() {
      return this.isDebugMode && this.data?.type !== 'intentAI';
    },
    hasBreakpoint() {
      return this.canToggleBreakpoint && this.debugBreakpoints.has(this.id);
    },
    isSetNextTarget() {
      return this.isDebugMode && this.debugOverrideNextNodeId === this.id;
    },
    showSetNextOverlay() {
      return this.isDebugMode && this.debugStatus === 'paused'
        && this.debugHoveredNodeId === this.id
        && this.debugCurrentNodeId !== this.id;
    },
    messageInputModels() {
      if (this.data?.type !== 'messageInput') return [];
      const fallbackSelectedIds = this.data?.selectedModel ? [this.data.selectedModel] : [];
      const selectedIds = Array.isArray(this.data?.selectedModel)
        ? this.data.selectedModel
        : fallbackSelectedIds;
      if (!selectedIds.length) return [];
      const options = this.aiModelOptions || [];
      return selectedIds.map(id => {
        const found = options.find(o => o.value === id);
        return {
          value: id,
          displayName: found ? found.name : `${id}`,
        };
      });
    },
  },
  methods: {
    getNodeHostElement() {
      const root = this.$el;
      if (!root) return null;
      if (root.nodeType === 1) {
        return root.closest('.vue-flow__node') || root;
      }
      return root.parentElement?.closest('.vue-flow__node') || root.parentElement || null;
    },
    updateBreakpointClass() {
      const host = this.getNodeHostElement();
      if (!host) return;
      if (this.hasBreakpoint) {
        host.classList.add('has-breakpoint-node');
      } else {
        host.classList.remove('has-breakpoint-node');
      }
    },
    clearDebugStateHostClass() {
      const host = this.getNodeHostElement();
      if (!host) return;
      host.classList.remove('debug-running-node', 'debug-success-node', 'debug-error-node', 'debug-paused-node');
    },
    updateDebugStateHostClass() {
      const host = this.getNodeHostElement();
      if (!host) return;
      this.clearDebugStateHostClass();
      const state = this.debugState;
      if (state && state !== 'idle') {
        host.classList.add(`debug-${state}-node`);
      }
    },
    toggleBreakpointFromButton() {
      if (typeof this.toggleDebugBreakpoint === 'function') {
        this.toggleDebugBreakpoint(this.id);
      }
    },
    toggleModelSelector() {
      this.showModelSelector = !this.showModelSelector;
    },
    onModelChange(value) {
      this.selectedModel = value;
      this.$emit('update:data', this.id, {
        selectedModel: value
      });
      this.showModelSelector = false;
    },
    getModelDisplayName() {
      // ใช้ข้อมูลจาก data แทน selectedModel
      if (this.data.provider && this.data.model) {
        return `${this.data.provider}: ${this.formatModelName(this.data.model)}`;
      } else if (this.data.provider) {
        return this.data.provider;
      } else if (this.data.model) {
        return this.formatModelName(this.data.model);
      }
      return 'Select Model';
    },
    getQueryRewriterModeDisplay() {
      const mode = this.data.queryRewriterMode || 'continueQuestion';
      if (mode === 'continueQuestion') {
        return 'Continue Question';
      }
      if (mode === 'historySummary') {
        return 'History Summary';
      }
      return mode;
    },
    formatModelName(modelName) {
      if (!modelName) return '';
      // ถ้ามี | ให้เอาส่วนหลัง | มาแสดง (เช่น gpt-4|gpt-4 จะแสดงแค่ gpt-4)
      if (modelName.includes('|')) {
        const parts = modelName.split('|');
        return parts[parts.length - 1];
      }
      // ตัดให้สั้นลงถ้ายาวเกินไป
      return modelName.length > 20 ? modelName.substring(0, 20) + '...' : modelName;
    },
    extractSiteNameFromUrl(siteUrl) {
      if (!siteUrl) return '';
      
      try {
        // แปลง URL string เป็น URL object
        const url = new URL(siteUrl);
        
        // ถ้าเป็น SharePoint URL แบบปกติ (เช่น https://company.sharepoint.com/sites/sitename)
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);
        
        // ถ้า path มี sites/ ให้เอาส่วนหลัง sites
        if (pathParts.length >= 2 && pathParts[0] === 'sites') {
          return pathParts[1];
        }
        
        // ถ้าไม่ใช่ รูปแบบ sites/ ให้ใช้ hostname แทน
        // เช่น https://company-my.sharepoint.com -> company
        const hostname = url.hostname;
        if (hostname.includes('.sharepoint.com')) {
          const subdomain = hostname.split('.')[0];
          // ถ้ามี - ให้เอาส่วนแรก
          return subdomain.split('-')[0];
        }
        
        // ถ้าไม่ตรงกับรูปแบบไหนเลย ให้ return hostname
        return hostname;
      } catch (error) {
        console.error(error);
        // error ถูกละเว้นโดยตั้งใจ: URL ไม่ถูกต้อง ให้ใช้ค่า fallback (return string เดิม)
        return siteUrl;
      }
    },
    // Smart Intent specific methods
    getSmartIntentActionModelDisplay(action) {
      if (!action || !action.useAI) return '';

      const provider = action.provider;
      const model = action.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },
    getSmartIntentFallbackModelDisplay() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback || !fallback.useAI) return '';

      const provider = fallback.provider;
      const model = fallback.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },

    // Knowledge Sources Detection Methods
    hasKnowledgeSources() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && (
          action.enableDocuments ||
          action.enableDatabaseIntegration ||
          action.enableProducts ||
          action.enableApiKnowledge
        );
      });
    },

    hasDocumentsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDocuments;
      });
    },

    hasDatabaseKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDatabaseIntegration;
      });
    },

    hasProductsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableProducts;
      });
    },

    hasApiKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableApiKnowledge;
      });
    },

    // Individual Smart Intent Knowledge Sources Detection
    hasSmartIntentKnowledgeSources(smartIntent) {
      if (!smartIntent || !smartIntent.mainAction) return false;

      const action = smartIntent.mainAction;
      return action.enableDocuments ||
        action.enableDatabaseIntegration ||
        action.enableProducts ||
        action.enableApiKnowledge;
    },

    // Smart Intent Fallback Knowledge Sources Detection
    hasSmartIntentFallbackKnowledgeSources() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback) return false;

      return fallback.enableDocuments ||
        fallback.enableDatabaseIntegration ||
        fallback.enableProducts ||
        fallback.enableApiKnowledge;
    },

    hasFallbackDocumentsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDocuments;
    },

    hasFallbackDatabaseKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDatabaseIntegration;
    },

    hasFallbackProductsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableProducts;
    },

    hasFallbackApiKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableApiKnowledge;
    },

    deleteNode() {
      // Add click.stop to prevent event bubbling
      this.$emit('delete-node', this.id);
    },
    getInputConnectionCount() {
      const edges = this.activeFlowEdges;
      return edges.filter(edge => edge.target === this.id && edge.targetHandle === 'input').length;
    },
    getMergeStrategyLabel(strategy) {
      const labels = {
        'concatenate': 'Join All',
        'summary': 'AI Summary'
      };
      return labels[strategy] || 'Combine';
    },
    getPromptPreview(prompt) {
      if (!prompt) return '';
      return prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt;
    },
    getIntentActionsCount(intent) {
      let count = 0;
      if (intent.mainAction) count++;
      if (intent.parallelActions) count += intent.parallelActions.length;
      return count;
    },
    getSwitchOutputsCount(switchData) {
      return switchData.switchOutputs ? switchData.switchOutputs.length : 0;
    },
    getJsonOperatorLabel(operatorType) {
      return getJsonConditionOperatorToken(operatorType) || operatorType || '';
    },
    getJsonConditionPreview(conditionData) {
      const conditions = Array.isArray(conditionData.conditions) ? conditionData.conditions : [];
      if (conditions.length === 0) return 'No rules';
      if (conditions.length > 1) return `${conditions.length} rules (${(conditionData.matchMode || 'all').toUpperCase()})`;

      const condition = conditions[0] || {};
      const path = condition.jsonPath || '$';
      const operatorLabel = this.getJsonOperatorLabel(condition.operator);
      const compareValue = condition.compareValue ? ` ${condition.compareValue}` : '';
      const preview = `${path} ${operatorLabel}${compareValue}`.trim();
      return preview.length > 24 ? `${preview.substring(0, 24)}...` : preview;
    },
    // Check if a handle is connected
    isHandleConnected(handleId, handleType = 'source') {
      const edges = this.activeFlowEdges;

      return edges.some(edge => {
        if (handleType === 'source') {
          return edge.source === this.id && edge.sourceHandle === handleId;
        } else {
          return edge.target === this.id && edge.targetHandle === handleId;
        }
      });
    },
    hasOrchestratorContinueConnection() {
      return this.isHandleConnected('output', 'source');
    },
    hasOrchestratorAgentsConnection() {
      return this.isHandleConnected('agents-bottom', 'source');
    },
    hasAgentToolsConnection() {
      return this.isHandleConnected('tools-bottom', 'source');
    },
    getOrchestratorModelShortName() {
      return this.formatModelName(this.data.model || this.data.modelDisplayId || '');
    },
    getAgentModelShortName() {
      return this.formatModelName(this.data.agentModel || this.data.model || '');
    },
    getToolName(toolId) {
      const tools = this.availableAgentTools || [];
      const tool = tools.find(item => item.id === toolId || item.ID === toolId);
      return tool ? (tool.name || tool.Name || 'Master') : 'Master';
    },
    getToolTypeLabel() {
      const type = (this.data.toolType || 'master').toString().toLowerCase();
      if (type === 'master') {
        return this.data.masterToolId ? this.getToolName(this.data.masterToolId) : 'Master';
      }
      if (type === 'api') return 'API';
      if (type === 'text') return 'Text';
      return type.toUpperCase();
    },
    getInternalNodeCount() {
      const nodes = Array.isArray(this.data.internalNodes) ? this.data.internalNodes : [];
      return nodes.filter(node => !['flowInput', 'flowOutput'].includes(node?.data?.type)).length;
    },

    // Check if current node is connected from a parallel action
    isConnectedFromParallelAction() {
      const edges = this.activeFlowEdges;

      // Find any edge that has this node as target
      const incomingEdge = edges.find(edge => edge.target === this.id);

      if (!incomingEdge) return false;

      // Check if the source handle contains patterns that indicate parallel action
      const sourceHandle = incomingEdge.sourceHandle;

      // Pattern: sourceHandle format is like "intentId-parallelId" for parallel actions
      // Main actions typically use format "intentId-main" or just "intentId"
      if (sourceHandle && sourceHandle.includes('-') && !sourceHandle.includes('-parallel-')) {
        return false;
      }
      return true;
    },

    // Remove outgoing edges from apiTrigger and contactAgent when connected from parallel action
    removeOutgoingEdgesIfFromParallelAction() {
      // Only apply this logic for apiTrigger and contactAgent nodes
      if (this.data.type !== 'apiTrigger' && this.data.type !== 'contactAgent') return;

      // Check if this node is connected from parallel action
      if (!this.isConnectedFromParallelAction()) return;

      const edges = this.activeFlowEdges;

      // Find all outgoing edges from this node
      const outgoingEdges = edges.filter(edge => edge.source === this.id);

      // If there are outgoing edges, remove them using the parent component method
      if (outgoingEdges.length > 0) {
        outgoingEdges.forEach(edge => {
          // Emit event to parent component to remove the edge
          this.$emit('remove-outgoing-edge', edge.id);
        });
      }
    },

    // Advanced Router: walk the intent tree and emit a flat list with depth + collapse metadata
    getAdvancedRouterAllOutputs(outputs, depth = 1, ancestorCollapsed = false) {
      const result = [];
      if (!Array.isArray(outputs)) return result;
      for (const output of outputs) {
        if (!output || !output.id) continue;
        const hasChildren = Array.isArray(output.subIntents) && output.subIntents.length > 0;
        const isCollapsed = !!this.advancedRouterCollapsed[output.id];
        result.push({
          id: output.id,
          intentName: output.intentName,
          description: output.description,
          depth,
          isLeaf: !hasChildren,
          isCollapsed,
          hiddenByParent: ancestorCollapsed,
        });
        if (hasChildren) {
          result.push(...this.getAdvancedRouterAllOutputs(
            output.subIntents,
            depth + 1,
            ancestorCollapsed || isCollapsed
          ));
        }
      }
      return result;
    },
    getVisibleOutputs(outputs) {
      return this.getAdvancedRouterAllOutputs(outputs).filter(entry => !entry.hiddenByParent);
    },
    toggleAdvancedRouterCollapse(id) {
      if (!id) return;
      this.advancedRouterCollapsed = {
        ...this.advancedRouterCollapsed,
        [id]: !this.advancedRouterCollapsed[id],
      };
    },

    // Get dynamic class for handles based on connection status
    getHandleClass(handleId, baseClass, handleType = 'source') {
      const classes = [baseClass];
      if (this.isHandleConnected(handleId, handleType)) {
        classes.push('connected');
      }
      return classes.join(' ');
    },
    // Add method to check if handle can accept connection
    isValidConnectionTarget(handleId, sourceNodeId, sourceHandleId) {
      // Get parent flow instance to access validation
      const flowInstance = this.$parent;
      if (!flowInstance || !flowInstance.validateConnection) {
        return true;
      }
      const mockConnection = {
        source: sourceNodeId,
        target: this.id,
        sourceHandle: sourceHandleId,
        targetHandle: handleId
      };
      const validation = flowInstance.validateConnection(mockConnection);
      return validation.isValid;
    },
    // Add visual feedback for handles
    // onHandleMouseEnter(handleElement, handleId) {
    //     // Add logic to show connection preview
    //     const isValid = this.isValidConnectionTarget(handleId);
    //     if (isValid) {
    //         handleElement.classList.add('valid-connection');
    //     } else {
    //         handleElement.classList.add('invalid-connection');
    //     }
    // },
    // onHandleMouseLeave(handleElement) {
    //     handleElement.classList.remove('valid-connection', 'invalid-connection');
    // }
  }
}
</script>

<style scoped>
/* N8N Style Node Base */
.n8n-node {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  /* Changed from hidden to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.n8n-node:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: #d1d5db;
  transition: all 0.2s ease;
}

/* Debug breakpoint toggle — visible, easy to click, and doubles as the red breakpoint dot. */
.debug-breakpoint-toggle {
  position: absolute;
  z-index: 80;
  width: 30px;
  height: 30px;
  border-radius: 100%;
  background: #ccd2db;
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.debug-breakpoint-toggle:hover {
  transform: scale(1.08);
}

.debug-breakpoint-toggle.debug-breakpoint-active {
  background: #f44336;
  color: transparent;
}

/* Debug execution state borders */
.debug-state-success {
  border-color: #4caf50 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.debug-state-error {
  border-color: #f44336 !important;
  box-shadow: 0 0 8px rgba(244, 67, 54, 0.4), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.debug-state-running {
  border-color: #2196f3 !important;
  box-shadow: 0 0 12px rgba(33, 150, 243, 0.5), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  animation: debug-running-pulse 1.2s ease-in-out infinite;
}
.debug-state-paused {
  border-color: #ff9800 !important;
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

@keyframes debug-running-pulse {
  0%, 100% { box-shadow: 0 0 12px rgba(33, 150, 243, 0.5), 0 4px 12px rgba(0, 0, 0, 0.1); }
  50% { box-shadow: 0 0 20px rgba(33, 150, 243, 0.7), 0 4px 12px rgba(0, 0, 0, 0.1); }
}

.selected .n8n-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Per-node-type selected glow — overrides the generic blue above via source order
   (S15-P1 · matches the icon/handle theme color of these node types). */
.selected .knowledge-graph-node {
  border-color: #7c3aed;
  box-shadow: 0 0 0 9px rgba(124, 58, 237, 0.521) !important;
}

.selected .waterfall-search-node {
  border-color: #0f766e;
  box-shadow: 0 0 0 9px rgba(15, 118, 110, 0.521) !important;
}

.node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep full space for icon only */
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #686e7c;
  transition: all 0.2s ease;
}

.node-body {
  position: absolute;
  /* Position outside the node box */
  top: calc(100% + 13px);
  /* Start just below the node box */
  left: 50%;
  transform: translateX(-50%);
  /* Center horizontally */
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  /* Don't interfere with node interactions */
  z-index: 10;
}

.node-subtitle {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.orchestrator-node-icon {
  background: #f5f3ff !important;
  color: #7c3aed !important;
}

.agent-node-icon {
  background: #ecfeff !important;
  color: #077c99 !important;
}

.tool-node-icon {
  background: #fff7ed !important;
  color: #c3490a !important;
}

.flow-tool-node-icon {
  background: #e0e7ff !important;
  color: #4f46e5 !important;
}

.flow-input-node-icon {
  background: #ecfdf5 !important;
  color: #04815b !important;
}

.flow-output-node-icon {
  background: #fef2f2 !important;
  color: #d62525 !important;
}

/* Legacy minimal nodes for backward compatibility */
.minimal-node {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 160px;
  cursor: pointer;
  overflow: visible;
  /* Changed from default to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.minimal-node:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.node-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-title {
  font-size: 16px;
  font-weight: 600;
  color: #51565f;
  margin-bottom: 2px;
  line-height: 1.2;
}

/* Modern Intent Node */
.intent-modern-node {
  background: white;
  border: 5px solid #c0a4ff;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(145, 27, 255, 0.699);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.intent-modern-node:hover {
  border-color: #a984ff;
}

.selected .intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Header Section */
.intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6164ed 0%, #8458ea 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.25);
}

.intent-info {
  flex: 1;
  min-width: 0;
}

.intent-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin-bottom: 2px;
}

.intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Intents Container */
.intents-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* Intent Table Styles */
.intent-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.handle-row {
  position: relative;
  height: 40px;
  border-radius: 15px !important;
}

.handle-row:last-child {
  border-bottom: none;
}

.intent-cell {
  padding: 8px 12px;
  vertical-align: top;
  width: 70%;
  max-width: 70%;
  border-radius: 15px !important;
}

.handle-cell {
  padding: 8px 16px 8px 0;
  vertical-align: middle;
  position: relative;
  width: 80px;
  border: none;
}

.intent-content {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
}

.intent-number {
  flex-shrink: 0;
}

.intent-details {
  flex: 1;
  min-width: 0;
  max-width: 120px;
}

.intent-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  line-height: 1.2;
}

.intent-prompt-preview {
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.output-count {
  margin-top: 4px;
}

.handle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  position: relative;
}

.handle-label {
  position: absolute;
  right: 10px;
  transform: translateY(-50%);
  height: 8px;
  text-wrap: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  padding-right: 8px;
}

.main-handle .handle-label {
  color: #8661c5;
  right: 15px;
}

.parallel-handle .handle-label {
  color: #127bb9;
  right: 15px;
}

/* Intent Actions Handles */
.intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.main-action-handle {
  background: #ffffff;
  border: 3px solid #9655fd;
}

.main-action-handle:hover {
  background: #9655fd;
}

.parallel-action-handle {
  background: #ffffff;
  border: 3px solid #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.parallel-action-handle:hover {
  background: #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

/* Connected styles for intent action handles */
.main-action-handle.connected {
  background: #9655fd !important;
}

.parallel-action-handle.connected {
  background: #1698e4 !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}


.intent-action-handle:hover::after {
  opacity: 1;
}

/* Node Type Specific Styles */
.message-node {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 4px solid #53ca7e;
  border-radius: 15px 50px 50px 15px;
  color: #166534;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.26);
  position: relative;
  width: 130px;
  height: 100px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.message-node:hover {
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
  border-color: #119400;
}


.message-node .node-icon {
  background: #d8f7e6;
  color: #107c38;
  border: 2px solid rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);
}

.webhook-trigger-node {
  background: linear-gradient(135deg, #fdf4ff 0%, #faf5ff 100%);
  border: 4px solid #c084fc;
  border-radius: 15px 50px 50px 15px;
  color: #6b21a8;
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.26);
  position: relative;
  width: 130px;
  height: 100px;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.webhook-trigger-node:hover {
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.12);
  border-color: #a855f7;
}

.webhook-trigger-node .node-icon {
  background: #f2e5fe;
  color: #8845c8;
  border: 2px solid rgba(168, 85, 247, 0.2);
  backdrop-filter: blur(10px);
}

/* Selected AI Models list below Message Input node (outside the node box) */
.message-models-list {
  position: absolute;
  top: calc(100% + 30px + 4px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 3px;
  pointer-events: none;
  z-index: 10;  
}

.message-models-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #afdccb 20%, #afdccb 80%, transparent 100%);
  width: 140px;
}

.message-models-section-label {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-weight: 700;
  font-size: 11px;
  color: #218647;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 0;
  margin-top: -4px;
}

.message-model-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #374151;
  white-space: nowrap;
  background-color: #cee6e0;
  border: 1px solid #53ca7e;
  border-radius: 10px;
  padding: 2px 6px;
  width:120px;
}

.message-model-icon {
  color: #166534 !important;
  flex-shrink: 0;
  margin-right: 2px;
  margin-bottom: 3px;
}

.message-model-no {
  font-weight: 500;
  font-size: 11px;
  color: #166534;
  flex-shrink: 0;
  min-width: 16px;
  text-align: right;
}

.message-model-name {
  font-size: 10px;
  color: #166534;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.api-node {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 4px solid #fbbf24;
  border-radius: 15px 50px 50px 15px;
  color: #92400e;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.1);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.api-node:hover {
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.15);
  border-color: #f59e0b;
}

.api-node .node-icon {
  background: #fdebb4;
  color: #9f5705;
  border: 2px solid rgba(245, 158, 11, 0.2);
  backdrop-filter: blur(10px);
}

.model-node {
  border-left: 4px solid #8b5cf6;
}

.model-node .node-icon {
  background: #f5f3ff;
  color: #7d53dd;
}

.model-node .node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep original padding for AI model */
}

.model-node .node-body {
  padding: 8px 12px 16px 12px;
  /* Keep original padding for AI model */
  text-align: center;
  background: rgba(249, 250, 251, 0.5);
  /* Keep background for AI model */
  border-top: 1px solid #f1f5f9;
  /* Keep border for AI model */
}

.model-node.expanded {
  min-width: 220px;
}

/* AI Agent Node - Modern Minimal Style #3b82f6*/
.ai-agent-node {
  background: #ffffff80;
  backdrop-filter: blur(10px);
  border: 5px solid #8bb8ff;
  border-radius: 16px;
  box-shadow: 3px 5px 5px rgb(11, 68, 255) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  max-width: 280px;
  width: 220px;
  height: 90px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: visible;
  margin: 15px;
}

.ai-agent-node:hover {
  box-shadow:
    0 4px 12px rgba(59, 130, 246, 0.15),
    0 0 0 1px rgba(59, 130, 246, 0.2);
  border-color: #5f92ff;
}

.selected .ai-agent-node {
  border-color: #2563eb;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

.intent-node {
  border-left: 4px solid #3b82f6;
}

.intent-node .node-icon {
  background: #eff6ff;
  color: #316ccd;
}

.response-node {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 4px solid #f87c7c;
  border-radius: 50px 15px 15px 50px;
  color: #991b1b;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.08);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.response-node:hover {
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.12);
  border-color: rgb(243, 50, 50);
}

.response-node .node-icon {
  background: #fee2e2;
  color: #c82323;
  border: 2px solid #fecaca;
  backdrop-filter: blur(10px);
}

.guardrail-ai-node {
  background: white;
  border: 5px solid rgba(19, 153, 26, 0.664);
}

.guardrail-ai-node:hover {
  border: 5px solid rgba(10, 190, 19, 0.664);
}

.guardrail-ai-node .node-icon {
  background: transparent;
  color: rgb(18, 136, 18);
  border: none;
}

.intent-ai-node {
  background: white;
  border: 5px solid rgba(149, 55, 211, 0.664);
}

.intent-ai-node:hover {
  border: 5px solid rgba(94, 10, 190, 0.664);
}

.intent-ai-node .node-icon {
  background: transparent;
  color: rgb(146, 45, 230);
  border: none;
}

.query-rewriter-node {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #e9d5ff 100%);
  border: 3px solid #8b5cf6;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(139, 92, 246, 0.3);
  transition: all 0.2s ease;
  position: relative;
  min-width: 280px;
  width: 280px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.query-rewriter-node:hover {
  border-color: #7c3aed;
  box-shadow: 2px 6px 12px rgba(139, 92, 246, 0.5);
}

.selected .query-rewriter-node {
  box-shadow: 0 0 0 6px rgba(139, 92, 246, 0.4) !important;
}

.query-rewriter-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 10px;
}

.query-rewriter-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.query-rewriter-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.query-rewriter-title {
  font-size: 16px;
  font-weight: 700;
  color: #6d28d9;
  line-height: 1.2;
}

.query-rewriter-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #6d28d9;
  line-height: 1.3;
}

.query-rewriter-indicators {
  display: flex;
  justify-content: flex-start;
  padding: 0 16px 16px;
}

.query-rewriter-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(124, 58, 237, 0.16);
  color: #6d28d9;
  font-size: 12px;
  font-weight: 600;
}

.database-node {
  background-color: #a6cbf0;
  border: 4px solid #ffffff;
}

.database-node .node-icon {
  background: #f0f9ff;
  color: #0a78aa;
}

/* Text Processor Node - Modern Minimal Style */
.text-processor-node {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px solid #a855f7;
  border-radius: 16px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(168, 85, 247, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  height: 90px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: visible;
  margin: 15px;
}

.text-processor-node:hover {
  transform: translateY(-1px);
  box-shadow:
    0 4px 12px rgba(168, 85, 247, 0.15),
    0 0 0 1px rgba(168, 85, 247, 0.2);
  border-color: #9333ea;
}

.text-processor-content {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
  width: 100%;
}

.text-processor-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #8b3fd4 0%, #9333ea 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.25);
}

.text-processor-text {
  flex: 1;
  min-width: 0;
}

.text-processor-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.025em;
}

.text-processor-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.condition-node {
  background: linear-gradient(135deg, #50ff50 0%, #069606 100%);
}

.condition-node .node-icon {
  background: transparent;
  color: #167038;
}

.json-condition-node {
  border-color: #13a172;
  border-width: 5px;
  border-style: solid;
}

.json-condition-node:hover {
  border-color: #0bc58a;
  box-shadow: 0 10px 22px rgba(16, 144, 104, 0.18);
}

.json-condition-node-icon {
  background: #ffffff;
  color: #0a873f;
}

.json-condition-node-body {
  padding-bottom: 14px;
}

.json-condition-preview {
  max-width: 78px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delay-node {
  background: linear-gradient(135deg, #7bd3ff 0%, #088fce 100%);
}

.delay-node .node-icon {
  background: #027bba;
  color: #ffffff;
}

.switch-node {
  background: linear-gradient(135deg, #ff6b6b 0%, #b10c0c 100%);
}

.switch-node .node-icon {
  background: #da324e;
  color: #ffffff;
}

.webhook-node {
  border-left: 4px solid #16a34a;
}

.webhook-node .node-icon {
  background: #f0fdf4;
  color: #12833c;
}

.email-node {
  border: 4px solid #cd3e3e;
  width: 170px;
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  position: relative;
  transition: all 0.3s ease;
}

.email-node:hover {
  border-color: #ef4444;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
}

.email-node .node-icon {
  position: relative;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #ffffff;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.email-node .icon-bg {
  position: relative;
  z-index: 1;
}

.email-node .action-badge {
  position: absolute;
  top: -8px;
  left: 8px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
  z-index: 10;
}

/* Inline PREVIEW tag for Email node — sits next to the provider chip so it doesn't collide
   with the floating delete button / ACTION badge. */
.email-node .email-preview-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: #f1ebfe;
  color: #5556cd;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.25);
  user-select: none;
  white-space: nowrap;
  line-height: 1;
  height: 18px;
}

.email-node .status-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
}

.email-node .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
  transition: all 0.3s ease;
}

.email-node .status-indicator.configured .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  animation: pulse-green 2s infinite;
}

.email-node .node-body {
  padding-top: 8px;
}

.email-node .node-details {
  position: absolute;
  bottom: 4px;
  right: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
  max-width: calc(100% - 16px);
}

.email-node .provider-tag {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  text-transform: uppercase;
}

.email-node .provider-tag.smtp {
  background: #dbeafe;
  color: #1d4ed8;
}

.email-node .provider-tag.mailgun {
  background: #fef3c7;
  color: #a55b05;
}

.email-node .cc-indicator {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #686e7c;
}

/* Merge Node Styles */
.merge-node {
  border: 4px solid #38bdf8;
  width: 110px;
  background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
  position: relative;
  transition: all 0.3s ease;
}

.merge-node:hover {
  border-color: #0ea5e9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.merge-node .node-icon {
  position: relative;
  background: linear-gradient(135deg, #0b7db0 0%, #0284c7 100%);
  color: #ffffff;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.merge-node .icon-bg {
  position: relative;
  z-index: 1;
}

.merge-node .merge-badge {
  position: absolute;
  top: -8px;
  left: 8px;
  background: linear-gradient(135deg, #0b7cb0 0%, #027bba 100%);
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.3);
  z-index: 10;
}

.merge-node .input-counter {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #0b7cb0;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.3);
  z-index: 5;
  min-width: 24px;
  justify-content: center;
}

.merge-node .node-body {
  padding-top: 8px;
}

.merge-node .node-details {
  position: absolute;
  bottom: 0px;
  right: 10px;
}

.merge-node .strategy-tag {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  text-transform: uppercase;
}

.merge-node .strategy-tag.concat {
  background: #dbeafe;
  color: #1d4ed8;
}

.merge-node .strategy-tag.summary {
  background: #f0fdf4;
  color: #12833c;
}

.merge-node .strategy-tag.custom {
  background: #fef3c7;
  color: #a55b05;
}

@keyframes pulse-green {

  0%,
  100% {
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  }

  50% {
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
  }
}

.api-trigger-node {
  border-left: 6px solid #0ea5e9;
  border-color: #0ea5e9;
}

.api-trigger-node:hover {
  border-color: #75d3ff;
}

.api-trigger-node .node-icon {
  background: #f0f9ff;
  color: #0a78aa;
}

.contact-agent-node {
  border-left: 6px solid #dc2626;
  border-color: #dc2626;
}

.contact-agent-node:hover {
  border-color: #ff8282;
}

.contact-agent-node .node-icon {
  background: #fef2f2;
  color: #d62525;
}

.documents-node {
  background-color: #dbeafe;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.documents-node:hover {
  border: 4px solid #76b2ff !important;
  transition: all 0.2s ease;
}

.documents-node .node-icon {
  background: #f0f9ff;
  color: #0369a1;
}

.database-integration-node {
  background-color: #dcfce7;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.database-integration-node:hover {
  border: 4px solid #67d89a !important;
  transition: all 0.2s ease;
}

.database-integration-node .node-icon {
  background: #f0fdf4;
  color: #12833c;
}

.knowledge-api-node {
  background-color: #ffecdb;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.knowledge-api-node:hover {
  border: 4px solid #ffca98 !important;
  transition: all 0.2s ease;
}

.knowledge-api-node .node-icon {
  color: #b24c26;
}

.products-node {
  background-color: #fef3c7;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.products-node:hover {
  border: 4px solid #ffd68b !important;
  transition: all 0.2s ease;
}

.products-node .node-icon {
  background: #fef3c7;
  color: #a55b05;
}

/* SharePoint Node Styling */
.sharepoint-node {
  background-color: #e6f7f8;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.sharepoint-node:hover {
  border: 4px solid #1a9ba1 !important;
  transition: all 0.2s ease;
}

.sharepoint-node .node-icon {
  background: #e6f7f8;
  color: #02787c;
}

/* Knowledge Graph / Waterfall Search Node Styling (S15-P1 · ported from ajt) —
   tinted background/border matching NodePanel.vue iconBg/iconColor */
.knowledge-graph-node {
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.knowledge-graph-node:hover {
  border-color: #7c3aed;
}

.waterfall-search-node {
  background: #e0f2f1;
  border-color: #99d6d0;
}

.waterfall-search-node:hover {
  border-color: #0f766e;
}

/* Default Case Output Styling */
.switch-default-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
  border-radius: 0 0 18px 18px;
}

.default-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.default-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.default-output-number {
  background: linear-gradient(135deg, #ca4c0a 0%, #bd5711 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(234, 88, 12, 0.3);
}

.default-output-details {
  flex: 1;
  min-width: 0;
}

.default-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #9a3412;
  line-height: 1.3;
  margin-bottom: 2px;
}

.default-output-description {
  font-size: 0.75rem;
  color: #ac3a0b;
  line-height: 1.2;
  font-style: italic;
}

.default-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.default-output-handle {
  position: relative !important;
  right: -16px;
  margin-top: 14px;
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* Handle Styles - Modern Color Design */
.input-handle {
  height: 25px;
  width: 15px;
  border-radius: 2px;
  background: #f5f8ff;
  border: 3px solid #3173ff !important;
  border: none;
  left: -2px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-handle.connected {
  background: #3173ff !important;
}

.input-response-handle {
  height: 18px;
  width: 18px;
  background: #f5f8ff;
  border: 3px solid #f87171 !important;
  border: none;
  left: -2px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-response-handle:hover {
  background: #f87171;
}

.input-response-handle.connected {
  background: #f87171 !important;
}

.output-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  margin-right: -3px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
}

.input-handle:hover {
  background: #3173ff;
  opacity: 1;
}

.output-handle:hover {
  background: #f59e0b;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Connected source handle styles - apply hover color when connected */
.output-handle.connected {
  background: #f59e0b !important;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.condition-true-output-handle {
  background: #006eff;
}

.condition-false-output-handle {
  background: #dc2626;
}

.json-condition-match-handle {
  width: 20px;
  height: 20px;
  margin-right: -2px;
  background: #ffffff !important;
  border: 4px solid #22c7b8;
  box-shadow: 0 4px 10px rgba(34, 199, 184, 0.22);
}

.json-condition-fallback-handle {
  width: 20px;
  height: 20px;
  margin-right: -2px;
  background: #ffffff !important;
  border: 4px solid #f59e0b;
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.22);
}

.json-condition-node .input-handle {
  width: 18px;
  height: 34px;
  left: -2px;
  border-radius: 4px;
  background: #ffffff;
  border: 4px solid #4f7cff !important;
  box-shadow: 0 4px 10px rgba(79, 124, 255, 0.18);
}

.json-condition-node .input-handle:hover {
  background: #eef4ff;
  border-color: #3569f6 !important;
  box-shadow: 0 6px 14px rgba(53, 105, 246, 0.22);
}

.json-condition-node .input-handle.connected {
  background: #3569f6 !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(53, 105, 246, 0.28);
}

.json-condition-match-handle:hover {
  background: #f0fdfa !important;
  border-color: #11998f;
  box-shadow: 0 6px 14px rgba(17, 153, 143, 0.28);
}

.json-condition-fallback-handle:hover {
  background: #fff7ed !important;
  border-color: #ea7c12;
  box-shadow: 0 6px 14px rgba(234, 124, 18, 0.26);
}

/* Special handle styles for start/end nodes */
.message-node .output-handle {
  background: #ffffff;
  border: 3px solid #21c90b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.6);
}

.message-node .output-handle:hover {
  background: #21c90b;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle {
  background: #ffffff;
  border: 3px solid #f87171;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 12px rgba(248, 113, 113, 0.3);
}

.response-node .input-handle:hover {
  background: #f87171;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle,
.api-node .output-handle {
  background: #ffffff;
  border: 3px solid #f59e0b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
}

.api-node .input-handle:hover,
.api-node .output-handle:hover {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle {
  position: absolute;
  bottom: -14px;
  width: 17px;
  height: 17px;
  background: #ffffff;
  border: 3px solid #2d70ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.multi-edge-handle:hover {
  background: #2d70ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.intent-model-handle:hover {
  background: #ab44ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

/* Additional connected handle styles for specific node types */
.message-node .output-handle.connected {
  background: #21c90b !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle.connected {
  background: #f87171 !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle.connected,
.api-node .output-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle.connected {
  background: #2d70ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle.connected {
  background: #ab44ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.condition-true-output-handle.connected {
  background: #006eff !important;
}

.condition-false-output-handle.connected {
  background: #dc2626 !important;
}

.json-condition-match-handle.connected {
  background: #11998f !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(17, 153, 143, 0.26);
}

.json-condition-fallback-handle.connected {
  background: #ea7c12 !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(234, 124, 18, 0.24);
}

.multi-edge-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #3575ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.multi-edge-target-handle:hover {
  background: #3575ff;
}

.multi-edge-target-handle.connected {
  background: #3575ff !important;
}

.intentai-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.intentai-target-handle:hover {
  background: #ab44ff;
}

.intentai-target-handle.connected {
  background: #ab44ff !important;
}

.knowledge-graph-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #7c3aed;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.knowledge-graph-target-handle:hover {
  background: #7c3aed;
}

.knowledge-graph-target-handle.connected {
  background: #7c3aed !important;
}

.waterfall-search-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #0f766e;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.waterfall-search-target-handle:hover {
  background: #0f766e;
}

.waterfall-search-target-handle.connected {
  background: #0f766e !important;
}

/* Output Labels - Base Styles */
.output-labels {
  position: absolute;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  z-index: 50;
}

/* Intent Labels - Below the node, no background */
.intent-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Model Labels - Below the node, with background */
.model-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Classifier Labels - Positioned for diamond shape */
.classifier-labels {
  top: 10px;
  right: -60px;
  height: 100%;
  justify-content: space-around;
}

/* Loop Labels - Below the node, no background */
.loop-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Switch Labels - Below the node, no background */
.switch-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Condition Labels - Mixed positioning (right and bottom), no background */
.condition-labels {
  height: 100%;
  right: -15px;
}

.json-condition-labels {
  top: 0;
  right: -100px;
  width: 85px;
  height: 100%;
}

.output-label {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  border: none;
  white-space: nowrap;
  text-align: center;
}

.json-condition-labels .output-label {
  position: absolute;
  left: 8px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.1;
  text-align: left;
  transform: translateY(-50%);
}

.json-condition-match-label {
  top: 29%;
  color: #10837b;
}

.json-condition-fallback-label {
  top: 79%;
  color: #b06105;
}

/* Intent Labels - No background */
.match-label {
  color: #0c855d;
  background: transparent;
}

.nomatch-label {
  color: #d53d3d;
  background: transparent;
}

.intent-list-item {
  position: relative !important;
  /* เปิดให้ children absolute ทำงาน */
  overflow: visible !important;
  /* ให้ลอยออกมาเห็นได้ */
  padding-right: 60px !important;
}

/* stack your red items vertically on the right */
.intent-action-list {
  position: absolute;
  right: 0;
  /* ชิดขอบขวาของ parent */
  top: 50%;
  /* จัดกึ่งกลางแนวตั้ง */
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
  pointer-events: none;
}

/* optional: tighten up the red dots */
.intent-action-list .v-list-item {
  width: 10px;
  height: 10px;
  margin-bottom: 0px;
  border-radius: 50%;
  background-color: red;
  padding: 0;
}

/* Dynamic Intent Output Labels */
.intent-labels .output-label {
  position: absolute;
  right: -50px;
  transform: translateY(-50%);
  color: #484aaf;
  background: #e0e3f6;
  border: 1px solid #d2d5f6;
  font-weight: 600;
  font-size: 10px;
  white-space: nowrap;
}

/* Different colors for multiple outputs */
.intent-labels .output-label-0 {
  color: #096245;
  background: #d8ebeb;
  border-color: #c2e6df;
}

.intent-labels .output-label-1 {
  color: #9e2d2d;
  background: #eee0e5;
  border-color: #eeced3;
}

.intent-labels .output-label-2 {
  color: #784d05;
  background: #efe9df;
  border-color: #efe0c8;
}

.intent-labels .output-label-3 {
  color: #6241ad;
  background: #e4e2f7;
  border-color: #dad3f7;
}

/* Additional colors for more outputs */
.intent-labels .output-label:nth-child(n+5) {
  color: #484aaf;
  background: #e0e3f6;
  border-color: #d2d5f6;
}

/* Modern Minimal Model Labels */
.model-output-label {
  color: #2655a0;
  background: #dce6f7;
  border: 1px solid #cadbf7;
  font-weight: 600;
  font-size: 11px;
}

.model-multi-label {
  color: #6241ad;
  background: #e4e2f7;
  border: 1px solid #dad3f7;
  font-weight: 600;
  font-size: 11px;
}

/* Classifier Labels - With background */
.classifier-right-label {
  color: #ffffff;
  background: #0c855d;
}

.classifier-bottom-label {
  color: #ffffff;
  background: #6b7280;
}

/* Loop Labels - No background */
.loop-continue-label {
  color: #937005;
  background: transparent;
}

.loop-exit-label {
  color: #937005;
  background: transparent;
}

/* Switch Labels - No background */
.switch-label-1 {
  color: #d73753;
  background: transparent;
}

.switch-label-2 {
  color: #d73753;
  background: transparent;
}

.switch-label-3 {
  color: #d73753;
  background: transparent;
}

/* Condition Labels - No background */
.condition-true-label {
  position: absolute;
  top: 0;
  color: #2655a0;
  background: #dce6f7;
  border: 1px solid #cadbf7;
  font-weight: 600;
  font-size: 11px;
}

.condition-false-label {
  position: absolute;
  bottom: 0;
  color: #9d2d2d;
  background: #f0e3e8;
  border: 1px solid #f1c1c6;
  font-weight: 600;
  font-size: 11px;
}


/* Selection States */
.minimal-node.selected {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Delete Button Styles - Enhanced */
.delete-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 900;
  /* Higher z-index than handles to ensure it's always on top */
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
}

.n8n-node:hover .delete-button,
.minimal-node:hover .delete-button,
.decision-container:hover .delete-button,
.ai-agent-node:hover .delete-button,
.orch-v2:hover .delete-button,
.agent-v2:hover .delete-button,
.intent-modern-node:hover .delete-button,
.smart-intent-modern-node:hover .delete-button,
.guardrail-ai-node:hover .delete-button,
.switch-modern-node:hover .delete-button,
.intent-ai-node:hover .delete-button,
.query-rewriter-node:hover .delete-button,
.json-condition-node:hover .delete-button,
.adv-router-node:hover .delete-button {
  opacity: 1;
}

/* Special positioning for diamond node */
.diamond-delete {
  top: -12px;
  right: -12px;
}

/* Warning Badge Styles */
.warning-badge {
  position: absolute;
  bottom: -15px;
  right: -8px;
  width: 35px;
  height: 35px;
  background: #f59e0b;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 950;
  /* Higher than handles but lower than delete button */
  box-shadow: 0 0px 20px rgba(245, 158, 11, 1);
}


.warning-badge .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
  animation: warningPulse 2s infinite;
}

.preview-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 3px 9px;
  background: #e0dcf7;
  color: #474aae;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  border-radius: 10px;
  border: 1px solid #d0c4f7;
  z-index: 950;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
}

/* Inline PREVIEW tag for Advanced Router (sits in the meta chip row, not floating) */
.adv-router-preview-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #f1ebfe;
  color: #5a5cda;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.25);
  user-select: none;
  white-space: nowrap;
  line-height: 1;
  height: 20px;
}

/* Floating TEST pill — top-left (right is reserved for the delete button) */
.adv-router-test-pill {
  position: absolute;
  top: -12px;
  left: -10px;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: linear-gradient(135deg, #0c8379 0%, #048096 100%);
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.7px;
  border-radius: 12px;
  border: 1.5px solid #ffffff;
  box-shadow: 0 3px 10px rgba(13, 148, 136, 0.45);
  z-index: 960;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.adv-router-test-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 5px 14px rgba(13, 148, 136, 0.55);
}

.adv-router-test-pill:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(13, 148, 136, 0.4);
}

.adv-router-test-pill-icon {
  margin-right: 4px;
  color: #ffffff !important;
}
/* Warning badge animation */
@keyframes warningPulse {

  0%,
  100% {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
    transform: scale(1.3);
  }
}

/* Focus states for accessibility */
.n8n-node:focus-within {
  outline-offset: 2px;
}

.decision-container:focus-within {
  outline-offset: 4px;
}

/* Dragging State Styles - Card-like lifting and rotating effect for individual nodes */
/* Use Vue Flow's built-in dragging classes for specific node dragging */
.vue-flow__node.dragging .n8n-node,
.vue-flow__node[data-dragging="true"] .n8n-node,
.n8n-node.dragging {
  transform: translateY(-8px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18) !important;
  z-index: 1000;
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container .decision-node,
.vue-flow__node[data-dragging="true"] .decision-container .decision-node,
.decision-container.dragging .decision-node {
  transform: translateY(-12px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .minimal-node,
.vue-flow__node[data-dragging="true"] .minimal-node,
.minimal-node.dragging {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container::before,
.vue-flow__node[data-dragging="true"] .decision-container::before,
.decision-container.dragging::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.5;
}

/* Gentle pulse animation for handles during drag */
@keyframes gentlePulse {

  0%,
  100% {
    opacity: 0.9;
  }

  50% {
    opacity: 1;
  }
}

/* Delete button gentle animation during drag for individual nodes */
.vue-flow__node.dragging .delete-button,
.vue-flow__node[data-dragging="true"] .delete-button,
.delete-button.dragging {
  transform: scale(1.1);
  opacity: 1;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  animation: gentleFloat 3s ease-in-out infinite;
}

/* Gentle floating animation for delete button */
@keyframes gentleFloat {

  0%,
  100% {
    transform: scale(1.1) translateY(0px);
  }

  50% {
    transform: scale(1.1) translateY(-2px);
  }
}

/* Node text gentle enhancement during drag for individual nodes */
.vue-flow__node.dragging .node-title,
.vue-flow__node[data-dragging="true"] .node-title,
.node-title.dragging {
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vue-flow__node.dragging .ai-agent-title,
.vue-flow__node[data-dragging="true"] .ai-agent-title,
.ai-agent-title.dragging {
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vue-flow__node.dragging .decision-label,
.vue-flow__node[data-dragging="true"] .decision-label,
.decision-label.dragging {
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Minimal dragging effects for start/end nodes */
.vue-flow__node.dragging .message-node,
.vue-flow__node[data-dragging="true"] .message-node,
.message-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.16) !important;
}

.vue-flow__node.dragging .api-node,
.vue-flow__node[data-dragging="true"] .api-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.18) !important;
}

.vue-flow__node.dragging .response-node,
.vue-flow__node[data-dragging="true"] .response-node,
.response-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.18) !important;
}

.vue-flow__node.dragging .intent-modern-node,
.vue-flow__node[data-dragging="true"] .intent-modern-node,
.vue-flow__node.dragging .smart-intent-modern-node,
.vue-flow__node[data-dragging="true"] .smart-intent-modern-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 8px 8px 15px rgba(98, 0, 255, 0.2) !important;
}

.vue-flow__node.dragging .ai-agent-node,
.vue-flow__node[data-dragging="true"] .ai-agent-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(11, 68, 255, 0.493) !important;
}

/* Prefer reduced motion for users that request it */
@media (prefers-reduced-motion: reduce) {

  .n8n-node,
  .minimal-node,
  .ai-agent-node,
  .intent-modern-node,
  .json-condition-node,
  .message-node,
  .api-node,
  .response-node {
    transition: none !important;
    animation: none !important;
  }
}

/* Modern Switch Node */
.switch-modern-node {
  background: linear-gradient(135deg, #fcf8f8 0%, #e2e8f0 100%);
  border: 2px solid #f1636379;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-width: 200px;
  cursor: pointer;
  margin: 15px;
  overflow: visible;
}

.switch-modern-node:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  border-color: #f34646;
}

.selected .switch-modern-node {
  border-color: #f34646;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Switch Header Section */
.switch-header-section {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(135deg, #c54d4d 0%, #e90000 100%);
  border-radius: 18px 18px 0 0;
}

.switch-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: transparent;
  color: white;
  box-shadow: 0 4px 12px rgba(241, 99, 99, 0.3);
}

.switch-info {
  flex: 1;
  min-width: 0;
}

.switch-title {
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 2px;
}

.switch-subtitle {
  font-size: 0.8rem;
  color: #ffffff;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Switch Outputs Container */
.switch-outputs-container {
  padding: 0;
}

.switch-table {
  width: 100%;
  border-collapse: collapse;
}

.switch-table .handle-row {
  border: none;
}

.switch-table .handle-row:hover {
  background: rgba(99, 102, 241, 0.04);
}

.switch-output-cell {
  padding: 8px 16px;
  vertical-align: middle;
  border: none;
}

.switch-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.output-number {
  flex-shrink: 0;
  background: linear-gradient(135deg, #c25050 0%, #c84b4b 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
}

.switch-output-details {
  flex: 1;
  min-width: 0;
}

.switch-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-output-condition {
  font-size: 0.75rem;
  color: #59687c;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}

.handle-content.switch-handle {
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}

.switch-output-handle {
  right: -16px;
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.switch-output-handle .handle-label {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(86, 88, 209, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.switch-output-handle:hover .handle-label {
  opacity: 1;
}


/* Intent Fallback Output Styling */
.intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f9edff;
  border-radius: 0 0 12px 12px;
}

.fallback-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.fallback-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.fallback-output-number {
  background: #9954d2 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(182, 12, 234, 0.3);
}

.fallback-output-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.fallback-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #42129a;
  line-height: 1.3;
  margin-bottom: 2px;
}

.fallback-output-description {
  margin-top: -6px;
  margin-right: -18px;
  font-size: 0.75rem;
  color: #7f0cc2;
  line-height: 1.2;
  font-style: italic;
}

.fallback-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.fallback-output-handle {
  position: relative !important;
  right: -19px;
  margin-top: 14px;
  background: #ffffff !important;
  border: 3px solid #f59e0b !important;
  width: 18px !important;
  height: 18px !important;
  cursor: pointer;
}

.fallback-output-handle:hover {
  background: #f59e0b !important;
}

/* Connected styles for switch and fallback handles */
.switch-output-handle.connected {
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.fallback-output-handle.connected {
  background: #f59e0b !important;
}

/* Smart Intent Node Styles - Based on Intent Node */
.smart-intent-modern-node {
  background: white;
  border: 5px solid #4f46e5;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(79, 70, 229, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
  overflow: visible;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.smart-intent-modern-node:hover {
  border-color: #3730a3;
  box-shadow: 2px 6px 12px rgba(79, 70, 229, 0.6);
}

.selected .smart-intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Smart Intent Header Section */
.smart-intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.smart-intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.smart-intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.25);
}

.smart-intent-info {
  flex: 1;
  min-width: 0;
}

.smart-intent-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.smart-intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Knowledge Sources Indicators */
.knowledge-sources-indicators {
  margin-top: 4px;
}

.knowledge-icons-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.knowledge-icon {
  transition: all 0.2s ease;
  cursor: help;
}

.knowledge-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Smart Intents Container */
.smart-intents-container {
  padding: 8px 0px 8px 16px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.smart-intent-table {
  width: 100%;
  border-collapse: collapse;
}

.smart-intent-cell {
  padding: 8px 0;
  vertical-align: middle;
  border: none;
}

.smart-intent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 10px;
}

.smart-intent-number {
  background: #4f46e5 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-details {
  flex: 1;
  min-width: 0;
}

.smart-intent-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.ai-config-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Smart Intent Action Handles */
.smart-intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.smart-intent-action-handle.main-action-handle {
  background: #ffffff;
  border: 3px solid #4f46e5;
}

.smart-intent-action-handle.main-action-handle:hover {
  background: #4f46e5;
}

.smart-intent-action-handle.main-action-handle.connected {
  background: #4f46e5 !important;
}

/* Smart Intent Fallback Output Styling */
.smart-intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #eef2ff;
  border-radius: 0 0 12px 12px;
}

.smart-intent-fallback-output .fallback-output-number {
  background: #4f46e5 !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-fallback-output .fallback-output-name {
  color: #312e81;
}

.smart-intent-fallback-output .fallback-output-description {
  color: #4338ca;
}

/* Smart Intent Model Handle */
.smart-intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #4f46e5 !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.smart-intent-model-handle:hover {
  background: #4f46e5;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.smart-intent-model-handle.connected {
  background: #4f46e5 !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.default-output-handle.connected {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* AI Agent Node Styles */
.ai-agent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.ai-agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4675bf 0%, #1857df 100%);
  color: white;
  flex-shrink: 0;
}

.ai-agent-text {
  flex: 1;
  min-width: 0;
}

.ai-agent-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.ai-agent-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  line-height: 1.3;
}

.base-prompt-indicator {
  position: absolute;
  bottom: 3px;
  right: 3px;
  display: flex;
  align-items: center;
  color: #008316;
  font-weight: 600;
  background: rgba(227, 255, 227, 0.95);
  padding: 1px 2px;
  border-radius: 5px;
  border: 1px solid rgb(76, 184, 93);
  box-shadow: 2px 2px 4px rgb(33, 116, 34);
  width: fit-content;
  z-index: 10;
}

.base-prompt-indicator span {
  line-height: 1;
}

.ai-agent-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.provider-info,
.model-info {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
}

.provider-info {
  color: #3472d8;
}

.model-info {
  color: #8458ea;
}

.provider-info .v-icon,
.model-info .v-icon {
  opacity: 0.7;
}

/* Responsive styles for Smart Intent */
@media (max-width: 768px) {
  .smart-intent-modern-node {
    min-width: 280px;
    width: 280px;
  }

  .smart-intent-content {
    gap: 8px;
  }

  .smart-intent-name {
    font-size: 0.8rem;
  }

  .ai-config-info {
    font-size: 10px;
  }
}

/* ============================================================
   Advanced Router Node Styles
   ============================================================ */
.adv-router-node {
  background: white;
  border: 5px solid #7c3aed;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(124, 58, 237, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.adv-router-node:hover {
  border-color: #6d28d9;
  box-shadow: 2px 6px 12px rgba(124, 58, 237, 0.6);
}

.selected .adv-router-node {
  box-shadow: 0 0 0 9px rgba(167, 139, 250, 0.5) !important;
}

.adv-router-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.adv-router-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.adv-router-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(124, 58, 237, 0.25);
}

.adv-router-info {
  flex: 1;
  min-width: 0;
}

.adv-router-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.adv-router-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adv-router-meta {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.adv-router-chip {
  font-size: 10px !important;
  height: 18px !important;
}

.adv-router-outputs {
  padding: 6px 0;
}

.adv-router-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px 6px 12px;
  position: relative;
  border-bottom: 1px solid #f8fafc;
  min-height: 32px;
}

.adv-router-row:last-child {
  border-bottom: none;
}

.adv-router-row.adv-router-parent {
  background: rgba(124, 58, 237, 0.04);
}

.adv-router-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  cursor: pointer;
  color: #6d28d9;
  flex-shrink: 0;
}

.adv-router-toggle:hover {
  background: rgba(124, 58, 237, 0.12);
}

.adv-router-spacer {
  display: inline-block;
  width: 18px;
  flex-shrink: 0;
}

.adv-router-depth {
  font-size: 10px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  border-radius: 4px;
  padding: 1px 5px;
  flex-shrink: 0;
}

.adv-router-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adv-router-output-handle {
  width: 18px !important;
  height: 18px !important;
  background: #7c3aed !important;
  border: 2px solid white !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.28);
}

.adv-router-output-handle.connected {
  background: #16a34a !important;
}

.adv-router-output-handle.hidden-child-handle {
  visibility: hidden;
}

.adv-router-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  font-size: 12px;
  color: #6b7686;
}

.adv-router-fallback {
  border-top: 1px solid #f1f5f9;
  padding: 8px 16px;
  background: linear-gradient(180deg, #fff7ed 0%, #fffbf3 100%);
  border-bottom-left-radius: 11px;
  border-bottom-right-radius: 11px;
}

.adv-router-fallback .fallback-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.adv-router-fallback .fallback-output-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.adv-router-fallback .fallback-output-name {
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
}

.adv-router-fallback .fallback-output-description {
  font-size: 10px;
  color: #92400e;
}

.orch-v2 {
  background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  border: 4px solid #ffffff;
  border-radius: 20px;
  min-width: 230px;
  max-width: 280px;
  padding: 0;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.15);
  position: relative;
  overflow: visible;
}

.orch-v2:hover {
  border: 4px solid #ec4899 !important;
  box-shadow: 0 8px 24px rgba(236, 72, 153, 0.25);
}

.selected .orch-v2 {
  border-color: #ec4899;
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.15), 0 8px 24px rgba(236, 72, 153, 0.25);
}

.orch-v2-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 6px;
}

.orch-v2-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(236, 72, 153, 0.35);
}

.orch-v2-header-text {
  flex: 1;
  min-width: 0;
}

.orch-v2-title {
  font-size: 13px;
  font-weight: 700;
  color: #831843;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orch-v2-provider {
  font-size: 10px;
  font-weight: 500;
  color: #9d174d;
  opacity: 0.7;
  margin-top: 1px;
  text-transform: capitalize;
}

.orch-v2-model-row,
.orch-v2-features {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  flex-wrap: wrap;
}

.orch-v2-placeholder {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin: 2px 10px 8px;
  background: #fce9f3;
  border: 1px dashed rgba(236, 72, 153, 0.25);
  border-radius: 8px;
  font-size: 10px;
  color: #9d174d;
  opacity: 0.65;
  font-style: italic;
}

.orch-v2-placeholder-icon {
  color: #ec4899;
  opacity: 0.5;
}

.orch-v2-model-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fbe1ef;
  color: #9d174d;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orch-v2-param {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f4e6f8;
  color: #6d28d9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  font-family: 'JetBrains Mono', 'Cascadia Code', monospace;
}

.orch-v2-feat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  transition: all 0.15s ease;
  cursor: default;
}

.orch-v2-feat.feat-multi { background: #eed6f3; color: #7537e0; }
.orch-v2-feat.feat-stream { background: #dfdff2; color: #02689d; }
.orch-v2-feat.feat-guard { background: #fbded7; color: #a44b08; }
.orch-v2-feat.feat-cache { background: #fcd9d8; color: #b03b0a; }
.orch-v2-feat.feat-json { background: #ead8f3; color: #4338ca; }
.orch-v2-feat.feat-time { background: #fbded7; color: #a34b08; }
.orch-v2-feat.feat-txn { background: #eed6f3; color: #7537e0; }
.orch-v2-feat.feat-memory { background: #fad4e8; color: #ba185b; }
.orch-v2-feat.feat-input-msg { background: #ead2ec; color: #6d28d9; }
.orch-v2-feat.feat-voice { background: #f9d4d9; color: #ac390b; }
.orch-v2-feat.feat-lang { background: #dedde6; color: #0a6d65; }

.orch-v2-prompt {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  padding: 5px 12px 8px;
  margin: 2px 8px 8px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  border: 1px solid rgba(236, 72, 153, 0.08);
}

.orch-v2-prompt-icon {
  color: #9d174d;
  opacity: 0.5;
  margin-top: 1px;
  flex-shrink: 0;
}

.orch-v2-prompt-text {
  font-size: 9px;
  color: #69707d;
  line-height: 1.4;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.orchestrator-agents-bottom-handle,
.agent-tools-bottom-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: -15px;
}

.orchestrator-agents-bottom-handle {
  border: 3px solid #ef4444;
}

.orchestrator-agents-bottom-handle:hover,
.orchestrator-agents-bottom-handle.connected {
  background: #ef4444 !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.orchestrator-labels,
.agent-labels {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.orchestrator-continue-label {
  position: absolute;
  right: -65px;
  top: 50%;
  transform: translateY(-50%);
  color: #2655a0;
  background: #dce6f7;
  border: 1px solid #cadbf7;
  font-weight: 600;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.orchestrator-agents-label {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: #9e2d2d;
  background: #eee0e5;
  border: 1px solid #eeced3;
  font-weight: 600;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.agent-v2 {
  background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
  border: 4px solid #ffffff;
  border-radius: 20px;
  min-width: 230px;
  max-width: 280px;
  padding: 10px 14px 8px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.15);
}

.agent-v2:hover {
  border: 4px solid #14b8a6 !important;
  box-shadow: 0 8px 24px rgba(20, 184, 166, 0.25);
}

.agent-v2-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.agent-v2-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(20, 184, 166, 0.3);
}

.agent-v2-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.agent-v2-title {
  font-size: 13px;
  font-weight: 700;
  color: #134e4a;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-v2-desc {
  font-size: 9px;
  color: #0f766e;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.agent-v2-model-line {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 600;
  color: #b05109;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-v2-model-line .v-icon {
  flex-shrink: 0;
  color: #f59e0b;
}

.agent-v2-model-inherit {
  color: #0b7a70;
  font-weight: 500;
  font-style: italic;
}

.agent-v2-features {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px;
}

.agent-v2-feat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.agent-v2-feat.feat-auto { background: #daeefa; color: #225bda; }
.agent-v2-feat.feat-all { background: #d5f5eb; color: #047552; }
.agent-v2-feat.feat-memory { background: #f0ebf0; color: #c12269; }
.agent-v2-feat.feat-time { background: #d9f4f8; color: #026ca2; }
.agent-v2-feat.feat-disabled, .feat-disabled { color: #636d7b; opacity: 0.8; }

.agent-tools-bottom-handle {
  border: 3px solid #f59e0b;
}

.agent-tools-bottom-handle:hover,
.agent-tools-bottom-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.agent-tools-label {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #efe5d6;
  border: 1px solid #f59e0b;
  color: #794e06;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.tool-node {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 3px solid #ffffff;
  border-radius: 16px;
  min-width: 100px;
  width: 100px;
  height: 100px;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.15);
}

.tool-node:hover {
  border: 3px solid #f59e0b !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.25);
}

.tool-node .node-icon.tool-node-icon {
  background: linear-gradient(135deg, #a26907 0%, #d97706 100%) !important;
  color: white !important;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
}

.tool-node .node-header,
.flow-tool-node .node-header {
  padding: 12px 8px 6px 8px;
}

.tool-node .node-body,
.flow-tool-node .node-body {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  padding: 0;
}

.tool-node .node-title {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.tool-node .node-subtitle {
  font-size: 9px;
  color: #b45309;
  text-transform: uppercase;
  font-weight: 500;
}

.flow-tool-node {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border: 3px solid #ffffff;
  border-radius: 16px;
  min-width: 120px;
  width: 120px;
  min-height: 100px;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(79, 70, 229, 0.15);
}

.flow-tool-node:hover {
  border: 3px solid #4f46e5 !important;
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.25);
}

.flow-tool-node .node-icon.flow-tool-node-icon {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%) !important;
  color: white !important;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  box-shadow: 0 3px 10px rgba(79, 70, 229, 0.3);
}

.flow-tool-node .node-title {
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.flow-tool-node .node-subtitle {
  font-size: 10px;
  color: #4a42d7;
  font-weight: 400;
}

.flow-tool-node .node-subtitle .unconfigured {
  font-style: italic;
  color: #4b4db7;
  opacity: 0.7;
}

.flow-tool-node .flow-tool-stats {
  margin-top: 4px;
}

.flow-tool-node .flow-tool-stats .v-chip {
  font-size: 10px;
  height: 18px;
}

.flow-state-node {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 176px;
  min-height: 92px;
}

.hexagon-shape {
  position: relative;
  width: 176px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%);
  transition: filter 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.24);
}

.flow-input-state .hexagon-shape {
  background: linear-gradient(135deg, #178640 0%, #12873d 100%);
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
}

.flow-input-state:hover .hexagon-shape {
  filter: brightness(1.15);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.5);
}

.flow-output-state .hexagon-shape {
  background: linear-gradient(135deg, #bd5711 0%, #ca4c0a 100%);
  box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
}

.flow-output-state:hover .hexagon-shape {
  filter: brightness(1.15);
  box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
}

.hexagon-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  min-width: 132px;
  text-align: center;
}

.state-label {
  font-size: 15px;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

.flow-state-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  display: block;
  width: 22px;
  height: 22px;
  background: #ffffff;
  border: 4px solid #3040ff;
  border-radius: 3px;
  transform-origin: center;
  transform: translate(-50%, -50%) rotate(45deg);
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transition: all 0.2s ease;
}

.flow-state-handle:hover,
.flow-state-handle.connected {
  background: #3040ff !important;
}

.flow-state-handle-anchor {
  width: 6px !important;
  height: 6px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  z-index: 100;
}
</style>